/* Dynamic Plot code copyright 2015 (c) Tuva Labs Inc. */
/**
* created by cmiller
*/

import Raphael from 'raphael';
import { hasMathJaxPattern } from '../../utils/Util';
import { PlotViewElements, PlotViewIcons } from "./dragHandler";
// import {Dyn, DragDropManager} from '../dynDataSet/dynMiscellany';
// import mediator from '../dynDataSet/mediatorUtil';
export const wrapDynPlotView = function(Dyn) {
	let mediator = Dyn.mediator;
	let DragDropManager = Dyn.DragDropManager;

	// TODO: complete these mode classes and move plot view mode controls to a separate controller object, to simplify PlotView class.
    /*class PlotModeMarqueeSelect {
        constructor( bboxes, batchSize, addToSelection ) {
            // start Marquee-select interaction mode
            // where a mouse down, then drag, then mouse up draws a rectangle to select cases that intersect the rectangle.
            // mutually exclusive of other interaction modes in the Plot View
            this.mode = "marquee-select";
            this.bboxes = bboxes;
            this.batchSize = batchSize;
            this.addToSelection = Boolean( addToSelection );
        }
    }*/
    /*class PlotModeDragMap {
        constructor( plotElement ) {
            this.mode = 'drag-map';
            this.plotElement = plotElement;
        }
    }*/
    class PlotViewMode {
        constructor() {
            // set plot to view-only, no changes allowed,
            // so we hide all buttons and interaction, except for a minimal set
            // like selecting cases, adjusting Stat Labels, adjusting Legend size.
            // typically happens on load of a saved plot
            this.wantViewOnlyPlot = Dyn.preferences.isViewOnly();
            this.isDrawOnly = Dyn.plotUtil.isDrawOnly();
        }
        showControls() {
            return ! this.wantViewOnlyPlot; /*|| this.isDrawOnly);*/ // hide all controls if viewOnly
        }
        showAxes() {
            return ! this.isDrawOnly;
        }
    }
    class PlotModeImageReady extends PlotViewMode {
        constructor( wantImageReady ) {
            super();
            // mode to use just before and after image capture
            // used to hide all controls that are not needed for a printed plot
            this.wantImageReady = Boolean( wantImageReady );
        }
        showControls() {
            // show controls if reverting Image Ready and not in View Only mode
            return super.showControls() && ! this.wantImageReady;
        }
    }
//Dyn.PlotModeMarqueeSelect = PlotModeMarqueeSelect;
//Dyn.PlotModeDragMap = PlotModeDragMap;
Dyn.PlotViewMode = PlotViewMode;
Dyn.PlotModeImageReady = PlotModeImageReady;

class PlotView extends Dyn.UndoHelper {

    constructor( dataSet, plotObj ) {
        super();
        Dyn.plotView = this; // accessor (to be removed at some point!)

        // modelling and controlling objects
        this.dataSet = dataSet; // reference to Dyn.DataSet
        this.plotObj = plotObj;
        this.plotShaper = null; // Dyn.PlotShaper
        this.casePositions = []; // case position data set by plotShaper [{x,y,isVisible,height,width},....]
        this.iconStyleUsed = Dyn.EIconStyle.kNoStyle; // style used current plot
        this.plotAnimator = new Dyn.PlotAnimator(); // animation timing helper
        this.subscriptions = [];
        this.paperSize = null; // if non-null, we are using a fixed paper size (draw mode only)
        this._savedPlotState = null; // optional saved plot state for soft reset (see restore())

        // Rafael elements
        this.paper = null;	// Rafael canvas
        this.icons = [];	// plot case icons, may be null or paper.circle, paper.rect, paper.path objects.
        this.outlines = []; // outlines of selected cases, if any
        this.adornments = null; // Dyn.PlotAdornments - visual elements controlled by each plot element
        this.backgroundRect = null; // inside of axes and margins and toolbar, size=this.bounds
        this.excludedCasesDisplay = []; // icons and text for displaying excluded cases (missing or filtered)
        this.excludedCasesRect = null;  // covering rectangle for excluded cases tooltip
        this.marqueeSelectRect = null;
        this.progressIndicator = null;
        this.caseHoverGlow = null;	// glow effect for case icon under mouse, if any.
        this.swapAxesButton = null;
        this.drawModeCoverRect = null;	// invisible cover rect over all plot elements, for drawing freehand lines
        this.sonificationRect = null;   // for identifying which case is being sonified

        // data cache
        this.viewModeData = null; // temporary view mode data, null===default view mode (select mode)

        // objects with view rectangles
        this.bounds = new Dyn.mathUtil.Rect( 0,0,0,0 ); // plot interior
        this.xAxis = null;	// horizontal (X) axis data
        this.yAxis = null;	// vertical   (Y) axis data
        this.y2Axis = null; // optional 2nd Y axis
        this.topMargin = null; //for title
        this.rightMargin = null; // for color key of selected attribute
    }

	// constants
	static #kHorizontal = true; // parameter to 'isHorizontal'
	static #kVertical = false;	 // parameter to 'isHorizontal'

    // settings for a visible outline when in special modes like Sampling or isDrawOnly
    static plotOutlineOpacity = 0.8;
    static plotOutlineWidth = 6;

    /** undo the init of the plot view */
    destroy() {
        // Remove subscribes
        Dyn.mediatorLocalRemoveAll(this.subscriptions);

        // destroy sub-objects
        this.progressIndicator?.destroy(); this.progressIndicator = null;
        this.plotAnimator?.destroy(); this.plotAnimator = null;
        this.plotShaper?.destroy(); this.plotShaper = null;
        this.xAxis?.destroy(); this.xAxis = null;	// horizontal (X) axis data
        this.yAxis?.destroy(); this.yAxis = null;	// vertical   (Y) axis data
        this.y2Axis?.destroy(); this.y2Axis = null; // optional 2nd Y axis
        this.topMargin?.destroy(); this.topMargin = null; //for title
        this.rightMargin?.destroy(); this.rightMargin = null; // for color key of selected attribute

        // destroy SVG layer in view
        Dyn.plotUtil.destroyPaperAndSVGObjects( this.paper );
        this.paper = null;

        // clear references
        this.dataSet = null; // reference to Dyn.DataSet
        this.plotObj = null;
        Dyn.plotView = null

        // notify dependent objects
        Dyn.mediator.publish("plot:destroy");
    }

	// Initialize the plot using dataSet
	init( optRestoreState, optRestoreOptions ) {
		//Dyn.time( 'Plotview:init', 'Start' );
		let thisPlotView = this,
			containerWidth = Math.max(0, this.plotObj.width()),
			containerHeight = Math.max(0, this.plotObj.height());

		// create the plotting area
		this.paper = Raphael( this.plotObj[0], containerWidth, containerHeight );
		this.adornments = new Dyn.PlotAdornments( this.paper );
		this.plotShaper = new Dyn.PlotShaper(
            new Dyn.MixupElement( Dyn ), this.adornments, this.bounds, this.dataSet.getNumCasesPreFilter(),
            this.dataSet, this.plotAnimator );
        this.backgroundRect = PlotViewElements.makeBackgroundRect( Dyn, this );

		this.marqueeSelectRect = this.paper.rect( 0, 0, 0, 0 ).
			attr({ fill:"none", stroke:"#000" /*, "stroke-dasharray":"5,5"*/ } ).hide();

		let excludedCasesFontSize = Dyn.preferences.getFontSize( Dyn.plotUtil.excludedCasesFontSize );
        this.excludedCasesRect = PlotViewElements.makeExcludedCasesRect( Dyn, this );
		this.excludedCasesDisplay.push( // [0] is num missing value cases text
			this.paper.text( 0, 0, "" ).
				attr({ /*stroke:"gray",*/ "font-size":excludedCasesFontSize } ).
				hide());
		this.excludedCasesDisplay.push( // [1] is num filtered cases text
			this.paper.text( 0, 0, "" ).
				attr({ /*stroke:"gray",*/ "font-size":excludedCasesFontSize } ).
				hide());
		this.excludedCasesDisplay.push( // [2] is missing value icon (circle)
			this.paper.circle( 0, 0, Dyn.plotUtil.missingCaseIconRadius  ).
				attr({stroke:"black", fill:"gray" }).
				hide());
		this.excludedCasesDisplay.push( // [3] is filter icon (path)
			this.paper.path( Dyn.plotUtil.getFilterIcon( Dyn.plotUtil.fileredCaseIconSize )).
				attr({stroke:"none", fill:"black" }).
				hide());
        this.excludedCasesDisplay.push( // [4] is ordered by attribute text
            this.paper.text( 0, 0, "" ).
            attr({ /*stroke:"gray",*/ "font-size":excludedCasesFontSize } ).
            data( "tooltip", "" ).
            hover(
                function orderByHoverIn( e ) {
                    Dyn.toolTip.show( this, e, { x:this.attrs.x, y:this.attrs.y, alignRight: true } );
                },
                function buttonHoverOut( e ) {
                    if( Dyn.toolTip.isMyElement( this ))
                        Dyn.toolTip.hide();
                }).
            hide());

        let closeBox = Dyn.plotUtil.makeCloseBox( this.paper, hoverInCloseBox, hoverOutCloseBox, clickedOnClosebox, '',
                Dyn.plotUtil.orderByCloseButtonId, Dyn.plotUtil.roundRectAxisButtonOptions());
        closeBox.data( "tooltip", Dyn.loc("remove Ordered by")).hide();
        this.excludedCasesDisplay.push( closeBox ); // [5] is Order by closebox

        // create swap axis button at 0,0; it is moved into position by updateSwapAxesButton()
        let buttonSize = Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.iconSize );
        this.swapAxesButton = Dyn.plotUtil.makeSquareButton(
            this.paper,
            Dyn.plotUtil.getSwapIcon( buttonSize ),
            buttonHoverIn, buttonHoverOut, clickedOnSwapAxis, false, Dyn.loc("Swap X and Y Axes"), Dyn.plotUtil.axisSwapButtonId)
            .data( "tooltip", Dyn.loc("Swap X and Y Axes"))
            .hide();
        // #341: click area is as large as possible without overlap of axis drop zones

        let swapClickSize = Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.kMinAxisThickness );
        this.swapAxesButton.getCoverElement().attr({ x:0, y:0, height:swapClickSize, width:swapClickSize });

		this.drawModeCoverRect = this.paper.rect( 0, 0, containerWidth, containerHeight ).
				attr({ stroke:"none", fill:"white", opacity: Dyn.plotUtil.plotOpacityInDrawMode } ).
				hover( drawHoverIn, drawHoverOut ).
				drag( drawMove, drawStart, drawEnd ).
				hide();
		this.sonificationRect = this.paper.rect( 0, 0, 0, 0 ).
            attr({ fill:"none", stroke:"gray", 'stroke-width': Dyn.plotUtil.sonifyBorderWidth } ).
            hide();
		this.progressIndicator = new Dyn.ProgressIndicator( this.paper );

		// create subviews (that share the same coordinate system
		this.xAxis = new Dyn.AxisView( PlotView.#kHorizontal, containerWidth, Dyn.plotUtil.kMinAxisThickness, this.paper );
		this.yAxis = new Dyn.AxisView( PlotView.#kVertical, Dyn.plotUtil.kMinAxisThickness, containerHeight, this.paper );
		this.topMargin = new Dyn.TopMarginView( 0, Dyn.plotUtil.kMinAxisThickness, this.paper );
		this.rightMargin = new Dyn.RightMarginView( Dyn.plotUtil.kMinAxisThickness, 0, this.paper );
		this.updatePlotBounds( true, containerWidth, containerHeight );

		// set up the titles and drop zones
		this.xAxis.initVisuals( this.paper.height*Dyn.plotUtil.kAxisMaxProportion, this.handleMouseOverAxis, this.handleMouseOutAxis, this.handleMouseUpOverAxis );
		this.yAxis.initVisuals( this.paper.width*Dyn.plotUtil.kAxisMaxProportion, this.handleMouseOverAxis, this.handleMouseOutAxis, this.handleMouseUpOverAxis );
		this.topMargin.initVisuals( Dyn.MixupElement.kDefaultTitle );
		this.rightMargin.initVisuals();

        //this.addOrRemoveY2Axis( true ); // create y2 axis only if plot needs it (e.g. restore)

		// handle plot resize
		Dyn.mediatorLocalSubscribe( this.subscriptions, "plot:containerResized", (args) => {
                // Add timeout to let toolbar resize vertically
                // So that vertical height can be calculated correctly for plotviewer
                setTimeout(() => {
                    this.handlePlotResize( args );
                }, 300);
            });
        Dyn.mediatorLocalSubscribe( this.subscriptions, "plot:contextMenuChange", (args) => this.handleContextMenuChange( args ));
        Dyn.mediatorLocalSubscribe( this.subscriptions, "records:preNotifyCasesSelected", (args) => this.handlePreNotifyCasesSelected(args));
		Dyn.mediatorLocalSubscribe( this.subscriptions, "records:setCasesSelected", (args) => this.handleCasesSelected(args));
		Dyn.mediatorLocalSubscribe( this.subscriptions, "records:setSelectedAttribute", (args) => this.handleAttributeSelected( args ));
		Dyn.mediatorLocalSubscribe( this.subscriptions, "records:setOrderByAttribute", (args) => this.handleOrderByAttribute( args ));
		Dyn.mediatorLocalSubscribe( this.subscriptions, "records:setCasesFiltered", (args) => this.handleCasesFiltered(args));
        Dyn.mediatorLocalSubscribe( this.subscriptions, "records:randomSamplesUpdated", (args) => this.handleSampleCases(args));

		// updated Attribute dataType
		Dyn.mediatorLocalSubscribe( this.subscriptions, "records:setAttributeMetadata", (args) => this.handleUpdateAttributeMetadata(args));
        Dyn.mediatorLocalSubscribe( this.subscriptions, "records:setAttributeValues", (args) => this.handleUpdateAttributeValues(args));

		// update Attribute display setting - show/hide, order of display
        Dyn.mediatorLocalSubscribe( this.subscriptions, "records:updateAttributesDisplay", (data) => this.handleAttributeDisplay(data) );

		// to glow the axis on hover starts
		Dyn.mediatorLocalSubscribe( this.subscriptions, "records:dragStart", (dragData) => {
		        this.highlightDropArea( dragData );
		    });
		Dyn.mediatorLocalSubscribe( this.subscriptions, "records:dragEnd", (dragData) => {
                this.resetHighlightDropArea( dragData );
            });

		// respond to axis button clicks and drags
		Dyn.mediatorLocalSubscribe( this.subscriptions, "axis:clickButton", function( axisData ) {
			thisPlotView.handleAxisButtonClick( axisData );
		});
		Dyn.mediatorLocalSubscribe( this.subscriptions, "axis:dragButton", function( axisData ) {
			thisPlotView.handleAxisButtonDrag( axisData );
		});
        Dyn.mediatorLocalSubscribe( this.subscriptions, "axis:setMinMax", function( axisData ) {
                thisPlotView.handleAxisSetMinMax( axisData );
            });
		Dyn.mediatorLocalSubscribe( this.subscriptions, "marginView:dragBoundary", function( marginData ) {
			thisPlotView.handleDragMargin( marginData );
		});
		Dyn.mediatorLocalSubscribe( this.subscriptions, "plot:dragDivider", function( dragData ) {
            thisPlotView.handleDragDivider( dragData );
		});
		Dyn.mediatorLocalSubscribe( this.subscriptions, "plot:updateAdornment", ( lineData ) => {
            thisPlotView.handleUpdatePlotAdornment( lineData );
		});
		Dyn.mediatorLocalSubscribe( this.subscriptions, "plot:removePlotElement", function( dragData ) {
            thisPlotView.removePlot( dragData.controlID ); // remove the empty plot element
		});
		Dyn.mediatorLocalSubscribe( this.subscriptions, "plot:startViewMode", function( modeData ) {
            thisPlotView.startViewMode( modeData );
		});
		Dyn.mediatorLocalSubscribe( this.subscriptions, "plot:stopViewMode", function( modeData ) {
            thisPlotView.stopViewMode( modeData );
		});
		Dyn.mediatorLocalSubscribe( this.subscriptions, "modelFunctions:applyFormula", function( functionData ) {
            thisPlotView.handleUpdatePlotFunction( functionData, false );
		});
		Dyn.mediatorLocalSubscribe( this.subscriptions, "modelFunctions:removeFormula", function( functionData ) {
            thisPlotView.handleUpdatePlotFunction( functionData, true );
		});
		Dyn.mediatorLocalSubscribe( this.subscriptions, "pref:newColorScheme", ( data ) => {
            this.handleColorSchemaChange( data );
        });
        // obj = {attrId, axis, action}
        // axis = x/y
		// action = add/remove
		Dyn.mediatorLocalSubscribe( this.subscriptions, "plot:addRemoveAttributeOnAxis", ( obj ) => {
            var axis = obj.axis === 'x' ? thisPlotView.xAxis : obj.axis === 'y'? thisPlotView.yAxis: thisPlotView.y2Axis;
            if(axis) {
                if (obj.action === 'add'){
                    this.dropAttribute(obj.attrID, axis, false);
                } else {
                    this.removeAttribute(axis);
                }
            }
		});
		Dyn.mediatorLocalSubscribe( this.subscriptions, "records:dragTouchMove", ( data ) => {
            this.handleTouchMove( data );
		});
		Dyn.mediatorLocalSubscribe( this.subscriptions, "records:dragTouchEnd", ( data ) => {
            this.handleTouchEnd( data );
		});
		Dyn.mediatorLocalSubscribe( this.subscriptions, "pref:newTheme", ( data ) => {
            this.handleColorThemeChange( data.themeName );
        });
        Dyn.mediatorLocalSubscribe( this.subscriptions, "pref:newFontSize", ( data ) => {
                this.handleFontOrDotSizeChange( data );
            });
        Dyn.mediatorLocalSubscribe( this.subscriptions, "pref:newDotSize", ( data ) => {
                this.handleFontOrDotSizeChange( data );
            });
        Dyn.mediatorLocalSubscribe( this.subscriptions, "pref:wantY2Axis", ( data ) => {
            if( data?.source !== "plotView" ) {
                this.refresh( { action: data?.wantY2Axis ? 'Y2AxisOn' : 'Y2AxisOff' } );
            }
        });
        Dyn.mediatorLocalSubscribe( this.subscriptions, "toolBar:enableDisableCustomIcon", ( data ) => {
                this.handleFontOrDotSizeChange( data );
            });
        Dyn.mediatorLocalSubscribe( this.subscriptions, "pref:newPrecision", ( data ) => this.handlePrecisionChange( data ));
        Dyn.mediatorLocalSubscribe( this.subscriptions, "toolBar:showHideLabelsOnStats", ( data ) => this.handleShowHideLabels( data ));
        Dyn.mediatorLocalSubscribe( this.subscriptions, "summary:selectCell", ( data ) => {
                if( this.plotShaper.selectAdornmentByValue( data )) {
                    // update plot and summary view to match selection change
                    this.handleUpdatePlotAdornment({ adornmentSelected:true, updatePlot:true });
                }
            });
        Dyn.mediatorLocalSubscribe( this.subscriptions, "summary:selectRow", ( data ) => {
                if( this.plotShaper.selectAdornmentByValue( data )) {
                    // update plot and summary view to match selection change
                    this.handleUpdatePlotAdornment({ adornmentSelected:true, updatePlot:true });
                }
            });

		// initialize the tooltip utility (one per plot, which gets positioned over any one element on hover-over)
		Dyn.toolTip = new Dyn.ToolTip( this.paper, this.plotObj );

		// set the click handler for background click in plot area
        //  to stop editing of text in this view, if any (e.g. title, annotation text)
        //  because blur event is not called for objects in paper
		this.backgroundRect.click( function( e ) {
			if( this.isInTextEditMode()) {
		        // Check if document active element is textArea, and
		        // event x, y in textarea boundingrect, if yes, ignore
		        // document.activeElement.tagName !== 'TEXTAREA' &&
		        // util.eventNotInInlineTextArea(e.x, e.y)
				this.stopViewMode();
            }
		}, this );

		// to protect from leaflet post removal event propogation
		this.backgroundRect.dblclick( function( event ) {
		    event.stopPropagation();
		});

		// if optRestoreState then restore, else load init mixup state
		if( optRestoreState ){
			this.restore( optRestoreState, optRestoreOptions );
		} else {
			// do initial plot view arrangement (compare to mixup())
			var whatChanged = { action: 'mixUp', iconPosition: true, viewSize: true, wantFastCycles: true };
			this.plotShaper.updateAxes( whatChanged, this.getSubviewRef() );
			this.updatePlotView( whatChanged );
		}
        !Dyn.preferences.isViewOnly() && setTimeout(()=>{
            /* #1899 workaround to fix bug in positioning of closebox in axis titles, when viewOnly is false;
               In Mac Chrome, FireFox, Safari, the string width of the title string is *sometimes* reported incorrectly
               the first time, e.g. on first load, or after clearing browser cache.
               But even a slight delay like a pause in the debugger or setTimeout() fixes this, so clearly something is being
               loaded (font?) but we don't get notification of the load. As a workaround, we instead re-render
               the axis titles and buttons after 1000ms delay, to correct the position2. --Craig 2019-11-19
             */
            let debugWidth1 = this.xAxis.titles.length ? this.xAxis.titles[0].bbox.width : 0;
            this.xAxis.updateTitles();
            this.yAxis.updateTitles();
            this.layerVisuals(); // fix layering after updateTitles()
            let debugWidth2 = this.xAxis.titles.length ? this.xAxis.titles[0].bbox.width : 0;
            console.log( "PlotView rerendered the axis titles;", ( debugWidth1 !== debugWidth2 ) ?
                    "this UPDATED the closeBox position ("+debugWidth1+"=>"+debugWidth2+")"
                    : "closeBox position unchanged" );
        }, 1000); // 1000ms works; 0ms does not

        let viewMode = new Dyn.PlotViewMode();
        if( ! viewMode.showControls()) { // hide plot controls if viewOnly or drawOnly mode.
            this.setImageReadyOrViewOnlyMode( viewMode );
        }

		// track visualization environment Initialization
		_gaq.push(['_trackEvent', 'Plot Initialized', 'Init']);
		//Dyn.time( 'Plotview:init', 'End' );

		function clickedOnSwapAxis( /*e*/ ) {
			thisPlotView.swapAxes();
			// retain keyboard focus after click, so Enter key can swap back
            Dyn.plotUtil.setAsKeyboardFocus( thisPlotView.swapAxesButton.getCoverElement() );
			// track the swap axes event
			_gaq.push(['_trackEvent', 'Plot Function', 'Click', 'swapAxes']);
		}
		function buttonHoverIn( e ) {
            e.currentTarget.style.cursor = Dyn.plotUtil.cursorPointer;
			this.attr( { fill: Dyn.plotUtil.axisButtonDownColor, stroke: Dyn.plotUtil.axisButtonDownColor } );
			Dyn.toolTip.show( this, e, {}/*{ x:this.attrs.x, y:this.attrs.y }*/);
		}
		function buttonHoverOut( e ) {
			e.currentTarget.style.cursor = Dyn.plotUtil.cursorDefault;
			this.attr( { fill: Dyn.plotUtil.axisButtonUpColor, stroke: "none" } );
			if( Dyn.toolTip.isMyElement( this ))
				Dyn.toolTip.hide();
		}
		function drawHoverIn( e ) {
			// Dyn.plotView.drawModeCoverRect.attr("cursor", "url(/static/img/icons/pencil.png) 4 12, auto");
			e.currentTarget.style.cursor = Dyn.plotUtil.cursorPencil;
		}
		function drawHoverOut( e ) {
			e.currentTarget.style.cursor = Dyn.plotUtil.cursorDefault;
		}
		function drawMove( dx, dy, x, y, event ) {
			var pos = Dyn.plotUtil.dragEventXYtoPaperXY( x, y, event, thisPlotView.backgroundRect );
			if( thisPlotView.isInDrawMode()) { // safety check
				thisPlotView.drawToolModeAddPoint( pos.x, pos.y, event );
			}
		}
		function drawStart( x, y, event ) {
			var pos = Dyn.plotUtil.dragEventXYtoPaperXY( x, y, event, thisPlotView.backgroundRect );
			if( thisPlotView.isInDrawMode()) {
				thisPlotView.drawToolModeAddPoint( pos.x, pos.y, event );
			}
		}
		function drawEnd( e ) {
			if( thisPlotView.isInDrawMode()) {
                thisPlotView.drawToolModePostAddPoint(); // set up text-edit event?
				thisPlotView.stopViewMode();
			}
		}
		// function clickOnExcludedCases( /*event*/ ) {
		//     // select the excluded cases so they are visible in table and 1st visible in cards
		//     var dataSet = thisPlotView.dataSet,
        //         casesSelectedData = { ids: [] };
		//     for ( var i=0, j=dataSet.getNumCasesPreFilter(); i<j; i++ ) {
		//         if( ! dataSet.isCaseNonMissingNonFiltered( i )) {
        //             casesSelectedData.ids.push( i );
        //         }
        //     }
        //     if( casesSelectedData.ids.length > 0 ) {
        //         mediator.publish( "records:casesSelected", casesSelectedData );
        //     }
        // }
        function clickedOnClosebox( /*e */ ) {
            // handler to remove Ordered by {attributename}
            var attributeData = { id: "", name: "", object: "plot" };
            mediator.publish( "toolBar:orderByAttribute", attributeData );
        }
        function hoverInCloseBox( e ) {
            e.currentTarget.style.cursor = Dyn.plotUtil.cursorPointer;
            this.attr( { fill: Dyn.plotUtil.axisButtonDownColor, stroke: Dyn.plotUtil.axisButtonDownColor } );
            Dyn.toolTip.show( this, e, { x:this.attrs.x, y:this.attrs.y, alignRight: true });
        }
        function hoverOutCloseBox( e ) {
            e.currentTarget.style.cursor = Dyn.plotUtil.cursorDefault;
            this.attr( { fill: Dyn.plotUtil.axisButtonUpColor, stroke: "none" } );
            if( Dyn.toolTip.isMyElement( this ))
                Dyn.toolTip.hide();
        }
	}

    addOrRemoveY2Axis(  ) {
	    // check to see if the current plot model can use a Y2 axis or not
	    let plotElement = this.plotShaper.findElement( Dyn.EPlotControl.kControlsY2Axis ),
            wantY2Axis = Dyn.preferences.wantY2Axis() && plotElement && plotElement.canUseY2Axis( this.getSubviewRef());

	    if( wantY2Axis ) {
	        // init axis
            if( ! this.y2Axis ) {
                console.log("adding Y2 Axis to plot view");
                this.y2Axis = new Dyn.Y2AxisView( Dyn.plotUtil.kMinAxisThickness, this.paper.height, this.paper );
                this.y2Axis.initVisuals( this.paper.width * Dyn.plotUtil.kAxisMaxProportion, this.handleMouseOverAxis, this.handleMouseOutAxis, this.handleMouseUpOverAxis );
                this.y2Axis.setTitle( Dyn.loc( Dyn.plotStrings.promptY2 ));
                this.yAxis.setDualAttributesRef( this.y2Axis );
                this.updatePlotBounds();
                mediator.publish( "plot:addRemoveY2Axis", { hasY2Axis:true });
            }
        } else {
	        if( this.y2Axis ){
                console.log("removing Y2 Axis from plot view");
                this.yAxis.setDualAttributesRef( null );
                this.y2Axis.destroy();
                this.y2Axis = null; // this enough if there are no other references to y2Axis object
                this.updatePlotBounds();
                mediator.publish( "plot:addRemoveY2Axis", { hasY2Axis:false });
            }
        }
	    return wantY2Axis;
    }

	/**
	 * Save the plot to a data object that can be JSONified
	 * @returns {{}} - data to be used by restore()
	 */
	save() {
		"use strict";
		var plotModel = {};

        if( Dyn.plotUtil.isDrawModeAuthoring()) { // save the paper size in authoring mode only
            // when saving a drawing, we want to restore to the same size paper
            plotModel.paperSize = {
                width: Math.max( 0, this.paper.width ),
                height: Math.max( 0, this.paper.height )
            };
        }
        if ( !Dyn.plotUtil.isDrawOnly() ) {
            // axisview
            plotModel.xAxisView = this.xAxis.save();
            plotModel.yAxisView = this.yAxis.save();
            if( this.y2Axis ) {
                plotModel.y2AxisView = this.y2Axis.save();
            }
            // marginview for legend
            plotModel.legendView = this.rightMargin.save();
        }
        // chart title
        plotModel.topView = this.topMargin.save();
		// shapes/elements in the plot
		plotModel.plotShaper = this.plotShaper.save();

		// save selected cases and active filters
		plotModel.dataSet = this.dataSet.save();

		return plotModel;
	}

	/**
	 * Change our plot to match the saved data.
     * @param savedData {*} - data from this.save()
     * @param [options] - optional parameters if any
     * @param [options.action] - optional string for undo menu
     * @param [options.isReloadablePlotState] - if true, restore and save the plot state for later
     */
	restore( savedData, options ) {
        Dyn.assert( savedData );
        options = options || {};

        // optionally save or restore the initial plot state (for soft reset of the plot)
        if( options.isReloadablePlotState ) {
            this._savedPlotState = savedData;
            console.log("PlotView.restore() is restoring and saving a plot state")
        }

		// restore selected cases and active filters
        if( savedData.dataSet ) {
            // For backward compatibility as we move selectedAttribute to dataset level instead of legendview restore.
            // add check for if savedData.legendView exists, then use it to restore selectedAttributeId
            if( ( !savedData.dataSet.selectedAttributeId ) && savedData.legendView && savedData.legendView.attributeID ) {
                savedData.dataSet.selectedAttributeId = savedData.legendView.attributeID;
            }

            this.dataSet.restore( savedData.dataSet, true /* deferPlotUpdate */ );
        }

        if( savedData.paperSize && Dyn.plotUtil.isDrawOnly()) {
            this.paperSize = savedData.paperSize;
            this.handlePlotResize( { container:this.plotObj });
        }

		if( savedData.xAxisView ) {
			this.xAxis.restore( savedData.xAxisView );
		}
		if( savedData.yAxisView ) {
			this.yAxis.restore( savedData.yAxisView );
		}
		if( savedData.plotShaper ) {
			this.plotShaper.restore( savedData.plotShaper, {
                xAxisRef:this.xAxis, yAxisRef:this.yAxis,
                y2AxisData:savedData.y2AxisView, /*may be null; y2Axis not yet restored*/
                hasSavedPlotTitle: this.topMargin.hasSavedPlotTitle( savedData.topView )
            } );
		}
        if( savedData.legendView ){
            this.rightMargin.restore( savedData.legendView );
            // Note: right margin/legendView does not save/restore the legend attribute so we must do it here
            // this is also necessary if responding to deleted attribute or restore after delete
            let selectedAttribute = this.dataSet.getSelectedAttribute();
            if( this.rightMargin.getAttribute() !== selectedAttribute ) {
                this.rightMargin.setSelectedAttributeLegend( selectedAttribute );
            }
        }
        if( savedData.topView ) {
            this.topMargin.restore( savedData.topView );
        }

        // add or remove the Y2 axis, if there is a plot that uses it
        this.addOrRemoveY2Axis( /*true*/ );
        if( this.y2Axis && savedData.y2AxisView ) {
            this.y2Axis.restore( savedData.y2AxisView );
        }

		// update plot model
		let whatChanged = { action: options.action || 'restore',
							iconPosition: true,
							viewSize: true };
		this.plotShaper.updateAxes( whatChanged, this.getSubviewRef() );
		this.updatePlotView( whatChanged );

		// update view mode
        this.topMargin.showHidePlotMenu( this.plotShaper.wantPlotContextMenu() );
		if( this.wantDragMapMode( this.plotShaper )) {
		    // if restoring a map plot, set the default mode to drag-map
		    let mapElement = this.plotShaper.findElement( Dyn.EPlotControl.kControlsMap );
            this.startViewMode( { mode:"drag-map", plotElement:mapElement });
        } else {
		    this.setDefaultViewMode();
        }

		// update ModelFunctions panel if no formulas were restored
        if( ! this.plotShaper.hasElement( Dyn.EPlotControl.kControlsPlotFunction )) {
            Dyn.FunctionUtil.deleteAllFunctions();
        }

        // Publish this event to update the value in casecard
        mediator.publish("records:plotStateRestored");
	}

    /**
     * Refresh and update the plot when certain external parameters are changed.
     * E.g. Dual Axis on/off, Attribute color change, change language, color scheme, etc.
     * Note: used only when there is no need for a specialized handler like handleCasesFiltered(), etc.
     * @param whatChanged {{*}} -- optional parameters for optimizing for certain changes
     */
	refresh( whatChanged ) {
        // update tooltips for current language; tooltip string should match this.init()
        this.excludedCasesDisplay[5].data( "tooltip", Dyn.loc("remove Ordered by"));
        this.swapAxesButton.data( "tooltip", Dyn.loc("Swap X and Y Axes"));

        // make any initial modifications to the plot, if necessary
        this.addOrRemoveY2Axis(); // add Y2 axis with prompt? remove it?
        // Save then Restore the plot; handles minor changes like swapping colors or languages
        //let savedPlot = this.save();
		//this.restore( savedPlot, whatChanged );
        this.xAxis.updateVisuals();
        this.yAxis.updateVisuals();
        this.y2Axis && this.y2Axis.updateVisuals();
        this.topMargin.updateVisuals();
        this.rightMargin.updateVisuals();
        this.updateSwapAxesButton();
        // update plot model
        whatChanged.action = whatChanged.action || 'refresh';
        whatChanged.iconPosition = true;
        whatChanged.viewSize = true;
        this.plotShaper.updateAxes( whatChanged, this.getSubviewRef() );
        this.updatePlotView( whatChanged );
	}

	// Retrun the underlying DOM id for the plotelement
	getPlotElementId() {
		"use strict";
		return this.plotObj[0];
	}

	/**
	 * updatePlotBounds() -- Update the plot and subview boundary rectangles,
	 * based on the container size and the size of each subview.
	 * We preserve the height of the horizontal subviews like xAxis and topMargin,
	 * and preserve the width of the vertical subviews like yAxis and rightMargin.
	 *
	 * @param isInit {Boolean} --true if this is the first call to updatePlotBounds()
	 * @param containerWidth
	 * @param containerHeight
	 */
	updatePlotBounds( isInit, containerWidth, containerHeight ) {

		const notDrawOnly = ! Dyn.plotUtil.isDrawOnly(), // if no plot, only annotations
              overallSpacer   = 10,
			  leftMarginInset = notDrawOnly ? -2 : 0, // inset 1 pixel to avoid erasing boundary line of plot (do with other subviews too?)
			  y1Spacer      = notDrawOnly ? 3 : 0,    // for some reason y axis intrudes
              y2Spacer      = this.y2Axis ? 6 : 0;  // whitespace between y2 axis and legend
        let bottomSpacer    = overallSpacer,	 // Empty space from the bottom of the SVG boundary
            leftSpacer      = overallSpacer + y1Spacer, // Empty space from the left of the SVG boundary, +3 for some reason Y axis intrudes
            rightSpacer     = overallSpacer, // Empty space from the right of the SVG boundary
            topSpacer       = overallSpacer, // Empty space from the top of the SVG boundary
			leftWidth       = minZero( this.yAxis.calcThickness()), // width of Y axis
            y2width         = minZero( this.y2Axis ? this.y2Axis.calcThickness() : 0 ),
            legendWidth     = minZero( this.rightMargin.calcThickness()),
            rightWidth      = minZero( y2width + y2Spacer + legendWidth ),
			topHeight       = minZero( this.topMargin.calcThickness()), // height of top margin / title
			bottomHeight     = minZero( this.xAxis.calcThickness()); // height of X axis

		function minZero( widthOrHeight ) {
		    return( Math.max( 0, widthOrHeight )); // avoid negative width or height, 0 is OK if parent view too small
        }

		// preserve existing container size if optional parameters undefined
		if ( !containerWidth ) {
			containerWidth = this.paper.width;
		}
		if ( !containerHeight ) {
			containerHeight = this.paper.height;
		}
		Dyn.assert( containerWidth >= 0 && containerHeight >= 0, "updatePlotBounds() error: negative container widths");

		this.topMargin.bounds.x      = leftWidth + leftSpacer;
		this.topMargin.bounds.y      = topSpacer;
		this.topMargin.bounds.width  = minZero( containerWidth - (leftWidth + leftSpacer + rightWidth + rightSpacer));
		this.topMargin.bounds.height = minZero( topHeight );
		this.topMargin.bounds.addDebugRect( this.paper );

		this.rightMargin.bounds.x      = minZero( containerWidth - legendWidth - rightSpacer );
		this.rightMargin.bounds.y      = topSpacer + topHeight;
		this.rightMargin.bounds.width  = minZero( legendWidth );
		this.rightMargin.bounds.height = minZero( containerHeight - (topHeight + topSpacer + bottomSpacer) );
		this.rightMargin.bounds.addDebugRect( this.paper );

		this.yAxis.bounds.x      = leftSpacer + leftMarginInset;
		this.yAxis.bounds.y      = topSpacer + topHeight;
		this.yAxis.bounds.width  = leftWidth;
		this.yAxis.bounds.height = minZero( containerHeight - (topHeight + topSpacer + bottomHeight + bottomSpacer));
		this.yAxis.bounds.addDebugRect( this.paper, "blue" );

		if( this.y2Axis ) {
		    // optional y2 axis on right
            this.y2Axis.bounds.x      = minZero( containerWidth - rightWidth - rightSpacer );
            this.y2Axis.bounds.y      = topSpacer + topHeight;
            this.y2Axis.bounds.width  = y2width;
            this.y2Axis.bounds.height = minZero( containerHeight - (topHeight + topSpacer + bottomHeight + bottomSpacer));
            this.y2Axis.bounds.addDebugRect( this.paper, "blue" );
        }

		// xAxis is at bottom
		this.xAxis.bounds.x      = leftWidth + leftSpacer;
		this.xAxis.bounds.y      = minZero( containerHeight - bottomHeight - bottomSpacer );
		this.xAxis.bounds.width  = minZero( containerWidth - (leftWidth + leftSpacer + rightWidth + rightSpacer));
		this.xAxis.bounds.height = minZero( bottomHeight );
		this.xAxis.bounds.addDebugRect( this.paper, "red" );

		// plot interior bounds, taking up interior rectangle after other views have taken their space
		this.bounds.x      = leftWidth + leftSpacer;
		this.bounds.y      = topHeight + topSpacer;
		this.bounds.width  = minZero( containerWidth - (leftWidth + leftSpacer + rightWidth + rightSpacer));
		this.bounds.height = minZero( containerHeight - (topHeight + topSpacer + bottomHeight + bottomSpacer));
		this.bounds.addDebugRect( this.paper, "purple" );

		// adjust the axis, margin, toolbar on resize
        // NOTE: to debug visually, set Dyn.debugViewRects = true;
		if ( !isInit ) {
			this.xAxis.updateVisuals();
			this.yAxis.updateVisuals();
			this.y2Axis && this.y2Axis.updateVisuals();
			this.topMargin.updateVisuals();
			this.rightMargin.updateVisuals();
		}
		this.backgroundRect.attr( {
			'x': 0,
			'y': 0,
			'width': containerWidth,
			'height': containerHeight
		} );
		this.drawModeCoverRect.attr( { // cover the entire plot when shown, so all other element hover/clicks are disabled.
			'x': 0,
			'y': 0,
			'width': containerWidth,
			'height': containerHeight
		} );

		this.updateSwapAxesButton();
	}

	/**
	 * listener for particular attribute is selected
	 * args : attrib, columnName details
	 */
	handleAttributeSelected( args ) {
        // NOTE: undo handled by Dyn.DataSet.setSelectedAttribute()
		"use strict";
		var thisPlot = Dyn.plotView,
			whatChanged = { action: 'handleAttributeSelected', iconColor: true },
            selectedAttribute = thisPlot.dataSet.getSelectedAttribute();

		// attribute selection may change thickness of right margin
		if( thisPlot.rightMargin.needsNewThickness()) {
			whatChanged.viewSize = true;
		}

		// update now (else caller should update plot later, if additional changes needed first)
		if( ! args.deferPlotUpdate ) {
            if( this.rightMargin.getAttribute() !== selectedAttribute ) {
                this.rightMargin.setSelectedAttributeLegend( selectedAttribute );
            }
			if( whatChanged.viewSize ) {
				thisPlot.plotShaper.updateAxes( whatChanged, thisPlot.getSubviewRef());
			}
			if( thisPlot.plotShaper.hasElement( Dyn.EPlotControl.kControlsCaseOrder ) ||
                thisPlot.plotShaper.hasElement( Dyn.EPlotControl.kControlsMultiCaseSections )) {
				whatChanged.iconPosition = true; // use slow animation with re-sorting of icon positions
			} else { // else use fast animation for icon color changes only.
				whatChanged.noAnimation = true;
			}

			thisPlot.updatePlotView( whatChanged );
		}
	}

    /**
     * Update plot when user selects a new attribute to order by in the menu
     * @param marginData
     */
    handleOrderByAttribute( orderByData ) {
        // NOTE: undo handled by Dyn.DataSet.setOrderByAttribute()
        "use strict";
        var whatChanged = { action: 'orderByAttribute',
                            viewSize: true, // to adjust plot title in this.topMargin to new "order by" display
                            iconPosition: true }; // to animate icons to their new position
        let selectedAttribute = this.dataSet.getSelectedAttribute();
        if( ! orderByData.deferPlotUpdate ) {
            // order by may be triggered by a newly selected attribute
            if( this.rightMargin.getAttribute() !== selectedAttribute ) {
                this.rightMargin.setSelectedAttributeLegend( selectedAttribute );
            }
            this.plotShaper.updateAxes( whatChanged, this.getSubviewRef() );
            this.updatePlotView( whatChanged );
        }
    }

    /**
     * [handleAttributesDisplay attribute display is been updated via casecard]
     * @param  {[list]} attributeList [list of attributes which is hidden]
     * @return {[None]}
     * Axis - Check if any axis has the attribute which is been hidden
     * Legend - Check if removed attribute is on the legend area
     */
	handleAttributeDisplay( attributeList ) {
		var thisPlot = Dyn.plotView,
            selectedAttribute = thisPlot.dataSet.getSelectedAttribute();
        let axes = [ this.xAxis, this.yAxis ];
		if( this.y2Axis ) {
		    axes.push( this.y2Axis ) ;
		}
		attributeList.attributes.forEach( (attrToHide) => {
            let hideAttribObj = Dyn.dataSet.getAttributeByID(attrToHide);
            axes.forEach( (axisView) => {
                let attributeIndex = axisView.allAttributes.findAttribute( hideAttribObj );
                if( attributeIndex >= 0) {
                    this.removeAttribute( axisView, attributeIndex );
                }
            });
		});

		// Check on legend - if found match, remove it.
		if( selectedAttribute && ( attributeList.attributes.indexOf(
			selectedAttribute.id) >= 0 ) ){
			mediator.publish( "records:attributeNameSelected", { id: "id", name: "", object: "card" } );
		}
    }

    /** listener for "records:preNotifyCasesSelected" */
    handlePreNotifyCasesSelected( args ) {
        /*  Clear any adornment selections (Reference Lines, etc) now,
            since we are about to get a "records:setCasesSelected" message.
            This ensures that the summary view update gets accurate adornment selection
            information it's case selection notifier is called first.
            Note that we expect this.handleCasesSelected to update the plot after this call.
         */
        this.plotShaper.clearSelectedAdornments();
    }

	/**
	 * Listener for "records:setCasesSelected"
	 */
	handleCasesSelected( args ) {
		"use strict";
		var updateXYAxes = args.isCategoryChange,
            updateLegend = args.isCategoryChange || args.isNumericRangeChange,
			whatChanged = {
		            action: 'handleCasesSelected', iconSelection: true,
                    isInDrag: args.isInDrag,
                    noAnimation:args.noAnimation
		        };

		if( ! args.deferPlotUpdate ) {
			if( updateXYAxes ) {
                // redraw category names with new selection status
                this.xAxis.updateVisuals();
                this.yAxis.updateVisuals();
            }
			if( updateLegend ) {
			    // redraw categories or numeric marquee selection
				this.rightMargin.updateVisuals();
			}
			this.plotShaper.clearSelectedAdornments();
			// redraw case icons with new selection status
			this.updatePlotView( whatChanged );
		}
	}

    handleColorSchemaChange( args ) {
        // note: no undo, just refresh colors
        this.xAxis.updateVisuals();
        this.yAxis.updateVisuals();
        this.topMargin.updateVisuals();
        this.rightMargin.updateVisuals(); // update legend color
        this.updatePlotView( { action:'newColorSchema', iconColor:true }); // update icon color
    }

    handleColorThemeChange( themeName ) {
        // note: no undo, just refresh colors
        // other color changes are via style change, so no need for explicit redraw
        Dyn.plotUtil.setThemeColors( themeName );
        this.xAxis.updateVisuals();
        this.yAxis.updateVisuals();
        this.topMargin.updateVisuals();
        this.rightMargin.updateVisuals();
        // To update plotview for theme change
        this.refresh({ action:'switchColorTheme' });
    }

    handleFontOrDotSizeChange( args ) {
        // note: no undo, just redraw to new size
        this.updatePlotView( { action:'newFontOrDotSize', viewSize:true }); // update subview sizes and reposition icons
    }

    handlePrecisionChange( data ) {
        // note: no undo, just redraw to new size
        if( data.source !== "plotView" ) {
            this.updatePlotView( { action: 'newPrecision', statPrecision: true, noAnimation: true } ); // recalculate/redisplay stats at given precision
        }
    }

    handleShowHideLabels( data ) {
        // note: no undo, just redraw with/without labels
        this.updatePlotView( {
            action: (data.showLabelsOnStats?'showLabelsOnStats':'hideLabelsOnStats'), adornmentPositionOnly:true, noAnimation:true });
    }

	/**
	 * listener for change to the case filter.
     * @param args {*}
     * @param args.deferPlotUpdate
     */
	handleCasesFiltered( args ) {
		"use strict";
		var whatChanged = { action: 'handleCaseFiltered', viewSize: true, iconPosition: true, wantFastCycles: true };

		if( ! args.deferPlotUpdate ) {
			this.plotShaper.updateAxes( whatChanged, this.getSubviewRef() );
			this.updatePlotView( whatChanged );
		}
	}

    /**
     * Listener for sampling cases in the dataSet
     * @param args
     */
    handleSampleCases( args ) {
        "use strict";
        var whatChanged = { action: 'handleSampleCases', metaData:true, viewSize: true, iconPosition: true, wantFastCycles: true };

        if( ! args.deferPlotUpdate ) {
            // update the stats in the plot, if needed (see issue #1216)
            let hasExistingSingleStat = args.statsChecked && args.statsChecked.length===1 &&
                    this.plotShaper.hasElement( Dyn.EPlotControl[args.statsChecked[0]] );
            if( args.statsChecked && ! hasExistingSingleStat ) { // values from RandomSampleComponent.render()
                args.statsChecked.forEach( (controlString) => {
                    //console.log("handleSampleCases statsChecked", controlString );
                    Dyn.assert( Dyn.EPlotControl[controlString], "unknown control string "+controlString );
                    let plotElement = Dyn.PlotElemHelper.create( Dyn.EPlotControl[controlString] );
                    if( plotElement ) {
                       this.plotShaper.addElement( plotElement );
                    }
                });
            }
            if( args.statsUnchecked && ! hasExistingSingleStat ) { // values from RandomSampleComponent.render()
                args.statsUnchecked.forEach( (controlString) => {
                    //console.log("handleSampleCases statsUnchecked", controlString );
                    Dyn.assert( Dyn.EPlotControl[controlString], "unknown control string "+controlString );
                    this.plotShaper.removeElement( Dyn.EPlotControl[controlString] );
                });
            }

            // handle title and attribute name changes
            if( args.plotTitle ) {
                this.topMargin.setTitle( args.plotTitle, true );
            }

            // add a color border to the plot view, while sampling
            {
                let isSampling = this.dataSet.caseSampler.hasSamples(),
                    plotOutlineColor = isSampling ?
                        (this.dataSet.caseSampler.isSampleSelected() ?
                        Dyn.plotUtil.kCaseInSampleColor : ( this.dataSet.caseSampler.isDistributionSelected() ?
                        Dyn.plotUtil.kCaseInDistributionColor :
                        Dyn.plotUtil.kCaseColor )) :
                        "none";
                this.backgroundRect.animate( {
                    stroke: plotOutlineColor, 'stroke-opacity':PlotView.plotOutlineOpacity,
                    'stroke-width':isSampling? PlotView.plotOutlineWidth : 0, },
                    Dyn.plotUtil.stdAnimationTimeMs, Dyn.plotUtil.stdAnimationEasing);
            }
            //this.topMargin.showHideSubtitle( showSubTitle );

            this.plotShaper.updateAxes( whatChanged, this.getSubviewRef() );
            this.updatePlotView( whatChanged );
        }
    }

	/**
	 * Listener for click on an axis button.
	 * @param axisData
	 */
	handleAxisButtonClick( axisData, isUndoRedo = false ) {
		var whatChanged = {
                action: 'axisButtonClick',
                wantFastCycles: axisData.wantFastCycles,
                noAnimation: axisData.noAnimation },
			subViewRef = this.getSubviewRef();

		if( isUndoRedo ) {
            // if called by this.undo(), this.redo(), caller saves undo step
        } else {
		    if( axisData.actionString ) {
                //Dyn.assert( typeof axisData.actionString === "string", "missing action strong for undo" );
                Dyn.undoManager.saveUndoStep( {
                    saveData:    Object.assign( { wantUndoOfClick: true }, axisData ),
                    restoreData: Object.assign( { wantUndoOfClick: false }, axisData ), // copy original parameters for redo
                    actionID:    'axisButtonClick',
                    actionString: axisData.actionString,
                }, this );
            } else {
		        //console.log("assuming no undo for button click of type ", axisData.type );
            }
        }

		// update plot model
		if( this.plotShaper.handleAxisButtonClick( whatChanged, subViewRef, axisData )) {
			if( whatChanged.viewSize ) {
				// plot elements says it changed something that affects axes or subviews
				this.plotShaper.updateAxes( whatChanged, this.getSubviewRef() );
			}
			// update plot view (visible elements) based on whatChanged flags
			this.updatePlotView( whatChanged );
		}
    }

	/**
	 * Update plot with new parameters from 'drag axis' buttons.
     * @param axisData { range {Dyn.mathUtil.Range}, isHorizontal {Boolean} }
     * @param isUndoRedo
     */
	handleAxisButtonDrag( axisData, isUndoRedo = false ) {
		var whatChanged = {
				action: 'axisButtonDrag',
				viewSize:true, // it is possible that change in tick marks might thicken axis.
				noAnimation: true },
			axisView = this.getAxisByID( axisData.axisID ),
            newRange, oldRange;
		Dyn.assert( axisData.axisID, "missing axisID");
		Dyn.assert( axisView );

        if( isUndoRedo ) {
            // if called by this.undo(), this.redo(), caller saves undo step
            newRange = new Dyn.mathUtil.Range( axisData.range.min, axisData.range.max );
        } else {
            newRange = new Dyn.mathUtil.Range( axisData.newRange.min, axisData.newRange.max );
            oldRange = new Dyn.mathUtil.Range( axisData.oldRange.min, axisData.oldRange.max );
            Dyn.undoManager.saveOrUpdateLastUndoStep( {
                    saveData:    { range: oldRange, axisID: axisData.axisID },
                    restoreData: { range: newRange, axisID: axisData.axisID },
                    actionID:    'axisButtonDrag',
                    actionString: Dyn.loc( "Adjust Axis" ),
                },
                this,
                // update the last undo step if dragging the same axis, no matter which button, they all change the range
                ( lastUndoData, newUndoData ) => {
                    return (lastUndoData.restoreData.axisID === newUndoData.restoreData.axisID);
                }
            );
        }
        axisView && axisView.setUserCustomizedMinMax( newRange ); // set new axis range if axis exists
        this.plotShaper.updateAxes( whatChanged, this.getSubviewRef() ); // adjust axes/subviews
        this.updatePlotView( whatChanged ); // redraw plot to new range
    }

    /**
     * Update plot with new parameters set axis min/max panel.
     * Use this.xAxis.getNumericAxisRange() and this.yAxis.getNumericAxisRange() to initialize parameters.
     * @param axisData { rangeX {Dyn.mathUtil.Range}, rangeY {Dyn.mathUtil.Range} }
     * @param isUndoRedo
     */
    handleAxisSetMinMax( axisData, isUndoRedo = false ) {
        var whatChanged = {
                action: 'axisSetMinMax',
                viewSize:true,  // it is possible that change in tick marks might thicken axis.
            },
            oldRangeX = this.xAxis.getNumericAxisRange(),
            oldRangeY = this.yAxis.getNumericAxisRange(),
            newRangeX = new Dyn.mathUtil.Range( axisData.rangeX.min, axisData.rangeX.max ),
            newRangeY = new Dyn.mathUtil.Range( axisData.rangeY.min, axisData.rangeY.max ),
            changed = false;

        if( oldRangeX.isFinite() && newRangeX.isFinite() && ! Dyn.mathUtil.equalRanges( oldRangeX, newRangeX )) {
            // if X-axis is numeric and new range is finite and different
            changed = this.xAxis.setUserCustomizedMinMax( newRangeX ) || changed; // set new axis range
        }
        if( oldRangeY.isFinite() && newRangeY.isFinite() && ! Dyn.mathUtil.equalRanges( oldRangeY, newRangeY )) {
            // if Y-axis is numeric and new range is finite and different
            changed = this.yAxis.setUserCustomizedMinMax( newRangeY ) || changed; // set new axis range
        }

        var saveData = { rangeX: oldRangeX, rangeY: oldRangeY };
        var restoreData = { rangeX: newRangeX, rangeY: newRangeY };

        if(this.y2Axis) {
            var oldRangeY2 = this.y2Axis.getNumericAxisRange(),
            newRangeY2 = new Dyn.mathUtil.Range( axisData.rangeY2.min, axisData.rangeY2.max );
            if( oldRangeY2.isFinite() && newRangeY2.isFinite() && ! Dyn.mathUtil.equalRanges( oldRangeY2, newRangeY2 )) {
                // if Y2-axis is numeric and new range is finite and different
                changed = this.y2Axis.setUserCustomizedMinMax( newRangeY2 ) || changed; // set new axis range
            }
            saveData.rangeY2 = oldRangeY2;
            restoreData.rangeY2 = newRangeY2;
        }

        if( changed ) {
            if( ! isUndoRedo ) {
                Dyn.undoManager.saveOrUpdateLastUndoStep( {
                        saveData: saveData,
                        restoreData: restoreData,
                        actionID: 'axisSetMinMax',
                        actionString: Dyn.loc( "Adjust Axis" ),
                    }, this );
            }
            this.plotShaper.updateAxes( whatChanged, this.getSubviewRef() ); // adjust axes/subviews
            this.updatePlotView( whatChanged ); // redraw plot to new range
        }
    }

	/**
	 * Update plot when user drags boundary line of legend.
     * @param marginData {*}
     * @param isUndoRedo -- true if we are being called by undo() or redo().
     */
	handleDragMargin( marginData, isUndoRedo ) {
	    // note: no undo. record undo action for this change?
		"use strict";
		var whatChanged = { action: 'dragMargin', viewSize: true, noAnimation: true };

		if( isUndoRedo ) {
            // undo or redo: don't create a new undo step,
            // instead set RightMarginView.legendThickness
            Dyn.assertFiniteNumeric( marginData.width );
            this.rightMargin.legendThickness = marginData.width; // set same parameter as RightMarginView.initVisuals
        } else {
		    // for initial "do", we want to create or update the undo step
            // this.rightMargin.legendThickness has already changed
            Dyn.assert( marginData.isHorizontal === false ); // assume only right margin is draggable
            Dyn.assertFiniteNumeric( marginData.oldWidth );
            Dyn.undoManager.saveOrUpdateLastUndoStep(
                {
                    saveData: {width: marginData.oldWidth},
                    restoreData: {width: marginData.newWidth},
                    actionID: 'dragMargin',
                    actionString: Dyn.loc( "Resize Legend" ),
                },
                this
                // no canUpdateCallBack, any previous 'dragMargin' undo can be updated
            );
        }
		this.updatePlotView( whatChanged ); // adjust the plot boundaries to new legend and redraw
    }

    /**
     * Update plot and summary view when user drags divider line
     * NOTE: also handles undo.
     * @param dragData
     */
    handleDragDivider( dragData, isUndoRedo ) {
        "use strict";
        var whatChanged = { action: 'dragDivider', adornmentPositionOnly: true, noAnimation: true };
        if( ! isUndoRedo ) {
            // save plot state for undo
            Dyn.assert( dragData.saveData && dragData.restoreData );
            Dyn.undoManager.saveUndoStep( {
                saveData: dragData.saveData,
                restoreData: dragData.restoreData,
                actionID: whatChanged.action,
                actionString: Dyn.loc( "Drag Divider" )
            }, this );
        }
        this.updatePlotView( whatChanged ); // need to recalculate the summary view when the dividers move
    }

    /**
     * Update plot when formula or constants have been added, changed, or removed in modelFunction panel,
     * or update both plot and modelFunction panel to undo or redo or restore formulas.
     * NOTE: does not add an undo step (not needed?)
     * @param functionData
     */
    handleUpdatePlotFunction( functionData, isRemove ) {
        "use strict";
        var plotFunctionElement = this.plotShaper.findElement( Dyn.EPlotControl.kControlsPlotFunction ),
            gridLinesElement = this.plotShaper.findElement( Dyn.EPlotControl.kControlsGridLines ),
            whatChanged = { action: 'updatedPlotFunction', adornmentPositionOnly: true,
                            noAnimation: functionData.fastResponse || functionData.isRestore };
        Dyn.assert( functionData.id || functionData.isRestore );

        if( plotFunctionElement ) { // plot element already exists, modify or remove it.
            if( ! functionData.noUndo ) {
                Dyn.assert( functionData.oldStateAllFunctions, "handleUpdatePlotFunction() did not get correct state from ModelFunction.jsx" );
                Dyn.assert( functionData.undoString && functionData.undoString.length > 0 );
                let savedState = plotFunctionElement.save( functionData.oldStateAllFunctions );
                Dyn.undoManager.saveOrUpdateLastUndoStep( {
                        saveData:  {
                            id: functionData.id,
                            changeHint: functionData.changeHint,
                            state: savedState,
                        },
                        restoreData: null,
                        actionID: "updatePlotFunction",
                        actionString: functionData.undoString,
                    },
                    this,
                    ( lastUndoData, newUndoData ) => {  // update last undo if changing the same function for the same reason
                        return  (lastUndoData.saveData.id           === newUndoData.saveData.id) &&
                                (lastUndoData.saveData.changeHint   === newUndoData.saveData.changeHint);
                    });
            }

            if( isRemove ) {
                // remove the formula with the given ID in the plot
                let numFormulas = plotFunctionElement.removeFormula( functionData.id );
                if( numFormulas===0 ) {
                    this.plotShaper.removeElement( Dyn.EPlotControl.kControlsPlotFunction );
                    plotFunctionElement = null;
                }
                this.updatePlotView( whatChanged );
                // this.removePlot( Dyn.EPlotControl.kControlsPlotFunction, false, undoString ); // note: this generates an undo step
            } else if( functionData.isRestore ) {
                // restore the formulas, or restore the state of no formulas (if functionData.restoreData===null)
                Dyn.assert( functionData.noUndo );
                Dyn.assert( plotFunctionElement.getNumFormulas() > 0 );
                plotFunctionElement.restore( functionData.restoreData ); // restore the functions, notify ModelFunctions panel
                if( plotFunctionElement.getNumFormulas()===0 ) {
                    this.plotShaper.removeElement( Dyn.EPlotControl.kControlsPlotFunction );
                    plotFunctionElement = null;
                }
                this.updatePlotView( whatChanged );
            } else {
                // update the formula with the given ID in the plot
                Dyn.assert( functionData.id );
                const isSingleFunctionUpdate = true; // efficiency hint for setFormula()
                plotFunctionElement.setFormula( functionData.id, functionData.formulaString, functionData.syntax, functionData.constants, functionData.lineColor, isSingleFunctionUpdate );
                this.updatePlotView( whatChanged );
            }
        } else { // plot element does not exist, create it if needed
            if( isRemove ) {
                Dyn.assert( Dyn.FunctionUtil.getNumFunctions() === 0 );
                console.log( "handleUpdatePlotFunction() ignoring remove function because there are none.");
            } else if( functionData.isRestore ) {
                // restore the plot element+formulas
                Dyn.assert( functionData.noUndo );
                plotFunctionElement = new Dyn.PlotFunctionElement( Dyn );
                this.plotShaper.addElement( plotFunctionElement );
                this.plotShaper.addElement( new Dyn.GridLinesElement() );
                plotFunctionElement.restore( functionData.restoreData ); // restore the functions, notify ModelFunctions panel
                this.updatePlotView( whatChanged );
            } else {
                // add the plot element + one formula (this is the first formula to be created)
                plotFunctionElement = new Dyn.PlotFunctionElement( functionData.id, functionData.formulaString, functionData.syntax, functionData.constants, functionData.lineColor );
                gridLinesElement = new Dyn.GridLinesElement();
                this.addPlot( plotFunctionElement, gridLinesElement ); // note: this generates an undo step
            }
        }
        Dyn.assert( plotFunctionElement===null || plotFunctionElement.getNumFormulas() > 0 );
    }

    /**
     * Update plot, summary view, undo state, and/or selection in response to a change in
     * adornment/statistic of plot.
     * E.g. user drags or changes color of a reference or movable line, or changes number of St.Devs.,
     * or edits a text adornment, or selects a category in the summary view.
     * Triggered by messages either from the summary view or from the plot adornment.
     * @param dragData
     */
    handleUpdatePlotAdornment( data, isUndoRedo ) {
        "use strict";
        var whatChanged = { action: 'updatedPlotAdornment', adornmentPositionOnly: true, noAnimation: true };
        if( data.toggleShowSquaresOnLines ) {
            // toggle visible squares on regression lines
            this.plotShaper.showHideSquaresOnLines( ! this.plotShaper.showSquaresOnLines );
            whatChanged.action = this.plotShaper.showSquaresOnLines ? 'showSquares':'hideSquares';
        }
        if( data.saveData && data.restoreData ) {
            // create an undo step; adornment should already have been changed
            Dyn.undoManager.saveOrUpdateLastUndoStep( {
                saveData: data.saveData, // plotElement.save() data pre-change
                restoreData: data.restoreData,   // plotElement.save() data post-change
                actionID: 'updatedPlotAdornment',
                actionString: data.actionString
            }, this, data.canUpdateCallBack || (()=>false) ); // if callback provided, combine sequential undo steps
        } else if ( isUndoRedo ) {
            // Handle undo/redo: we are passed the .saveData or .restoreData, now update the adornment
            Dyn.assert( typeof data.primaryType === 'string' ); // type Dyn.EPlotControl
            let plotElement = this.plotShaper.findElement( data.primaryType );
            if( plotElement ) {
                plotElement.restore( data );
            }
        }
        if( !(data.updatePlot===false) ) { // unless set to false
            // recalculate the measures for the summary view, to update colors, divider locations, etc.
            this.updatePlotView( whatChanged );
        }
        if( data.adornmentSelected ) {
            // notify of summary view of selection change (otherwise no summary updates if no measures changed)
            Dyn.mediator.publish("plot:adornmentSelected",{});
        }
    }

	/**
	 * Get references to our subviews for use by Dyn.PlotShaper that might reference or manipulate them.
	 * @returns {{xAxisView: (null|*), yAxisView: (null|*), topMargin: *, rightMargin: *}}
	 */
	getSubviewRef() {
		"use strict";
		return {
			'xAxisView': this.xAxis,
			'yAxisView': this.yAxis,
            'y2AxisView': this.y2Axis,
			'topMargin': this.topMargin,
			'rightMargin': this.rightMargin
		};
    }

    getAxisByID( axisID ) {
        switch( axisID ) {
            case 'x':   return this.xAxis;
            case 'y':   return this.yAxis;
            case 'y2':  return this.y2Axis;
            case 'l':   return this.rightMargin; // aka legend
            case 't':   return this.topMargin;
            default:
                return null;
        }
    }


	//---------- Event handlers for drag/drop

	/**
	 * When the drag event is started from card, table, or any other source
	 */
	highlightDropArea( dragData ) {
		"use strict";
        // console.log('plotView.highlightDropArea');
		var dragObject = JSON.parse( dragData ),
			draggedAttribute = dragObject ? this.dataSet.getAttributeByID( dragObject.value ) : null;
		Dyn.assert( draggedAttribute === null || ( dragObject && dragObject.type === 'columnName'));

		// if a drag has started while in draw mode, get out of draw mode, but stay in drag map mode for map plots
        this.setDefaultViewMode();

        // highlight the drop areas, and expand them temporarily if narrow
        this.xAxis.highlightDropAreas( draggedAttribute, Dyn.axisUtil.getDropAreaThicknessChange( this.xAxis, this.yAxis, this.y2Axis, this.rightMargin ));
        this.yAxis.highlightDropAreas( draggedAttribute, Dyn.axisUtil.getDropAreaThicknessChange( this.yAxis, this.xAxis ));
        this.y2Axis && this.y2Axis.highlightDropAreas( draggedAttribute, Dyn.axisUtil.getDropAreaThicknessChange( this.y2Axis, this.xAxis, null, this.rightMargin ));
        this.rightMargin.highlightDropAreas( draggedAttribute );

        // put drop layer above cases temporarily so cases near expanded axis don't interfere
        this.xAxis.layerVisuals(1);
        this.yAxis.layerVisuals(1);
        this.y2Axis?.layerVisuals(1);
        this.rightMargin.layerVisuals();
    }

    /** undo highlightDropArea() temporary changes at end of drag */
	resetHighlightDropArea( /*obj*/ ) {
        // console.log('plotView.resetHighlightDropArea');
		"use strict";
        this.xAxis.resetHighlightDropAreas();
        this.yAxis.resetHighlightDropAreas();
        this.y2Axis && this.y2Axis.resetHighlightDropAreas();
        this.rightMargin.resetHighlightDropAreas();

        this.layerVisuals(); // undo layering changes
    }

	/**
	 * handle plot resize, adjust the axis and toolbar accordingly.
	 */
	handlePlotResize( obj ){
		// reset the paper canvas
        if( this.paperSize && ! Dyn.plotUtil.isDrawModeAuthoring() ) {
            // if this is a draw-only plot, saved with a fixed paper size, not in authoring mode
            // change the paper size; axes scale to paper, and annotations scale to axis
            Dyn.assert( Dyn.plotUtil.isDrawOnly() );
            let scale = Math.min(
                (obj.container.width() / this.paperSize.width),
                    (obj.container.height() / this.paperSize.height)).toPrecision(5); // 1.0===100% zoom
            this.paper.setSize( this.paperSize.width * scale, this.paperSize.height * scale );
            Dyn.preferences.setFontSizeMultiplier( scale, false /* defer update */ ); // scale text
            // console.log("zoom", scale, (Math.round(100*scale))+"%",
            //     "drawing w x h:", this.paperSize.width, this.paperSize.height,
            //     "paper:", this.paper.width, this.paper.height,
            //     "view:", obj.container.width(), obj.container.height());
        } else {
            // ordinary plot, or unsaved draw-only plot, sized to its container
            this.paper.setSize( obj.container.width(), obj.container.height());
        }
        this.updatePlotBounds( false /* don't initialize */ );
        // show border of drawing area in draw mode
        this.backgroundRect.attr( {
            stroke: Dyn.plotUtil.kCaseSubtleBorder,
            'stroke-opacity':PlotView.plotOutlineOpacity,
            'stroke-width':Dyn.plotUtil.isDrawOnly()? PlotView.plotOutlineWidth : 0, });

		// update plotview
		var whatChanged = { action: 'resize',
							viewSize: true,
							noAnimation: true };
        this.plotShaper.updateAxes( whatChanged, this.getSubviewRef() );
		this.updatePlotView( whatChanged );
    }

    handleContextMenuChange( args ) {
        // respond to a plot element's request to update the plot view;
        // called by the plot element's handleContextMenuClick() callback function as needed
        Dyn.assert( args.action );
        if( args.action==='addPlot') {
            // add a plot element exactly like ToolbarComponent.addPlotClick()
            let primaryElement = Dyn.PlotElemHelper.create( args.primaryType ),
                optionalExtraElement = null;
            if( args.extraType && ! this.plotShaper.hasElement(args.extraType) ) {
                optionalExtraElement = Dyn.PlotElemHelper.create( args.baseType, args.extraType );
            }
            this.addPlot( primaryElement, optionalExtraElement, args.noAnimation );
            Dyn.assert( this.plotShaper.hasElement(args.primaryType) );
            Dyn.assert( args.extraType && this.plotShaper.hasElement(args.extraType));
        } else if( args.action==='togglePlot') {
            // add a plot element exactly like ToolbarComponent.modifyTogglePlotClick()
            Dyn.assert( args.controlType && args.baseType );
            let existingPlotElem = this.plotShaper.findElement( args.baseType );
            if( existingPlotElem ) {
                // modify the boolean control type in the base class, possibly removing the element entirely
                this.togglePlotElement( args.baseType, args.controlType, args.actionName, args.noAnimation );
            } else {
                // if the element does not exist create it with modification
                let baseElement = Dyn.PlotElemHelper.create( args.baseType, args.controlType );
                this.addPlot( baseElement );
            }
        }
    }

	/**
	* Indicate that the mouse is over the dropable area
	*/
	handleMouseOverAxis( event ) {
		"use strict";
  		// console.log('plotView.handleMouseOverAxis');
  		// Check the current axis and see if style needs to be updated
  		var plotView = Dyn.plotView,
            dataSet = plotView.dataSet,
            dragData = DragDropManager.getDraggedValue(),
            dragObject = dragData && JSON.parse( dragData ),
			draggedAttribute = dragObject && dragObject.value && dataSet.getAttributeByID( dragObject.value ),
			isExtraDropRect = this.data('isExtraDropRect' ),
            axisID = event && event.target.getAttribute('axisID'),
            axisView = plotView.getAxisByID( axisID );
		Dyn.assert( draggedAttribute === null || dragObject.type === 'columnName' );

        if( draggedAttribute ) {
            axisView.mouseOverDroppableAreas( draggedAttribute, isExtraDropRect, event );
            // update all axis styles when mouse over one of them
            // [ plotView.xAxis, plotView.yAxis, plotView.y2Axis, plotView.rightMargin ].forEach((otherAxis) => {
            //     if( otherAxis && otherAxis===plotView.rightMargin ) {
            //         otherAxis.mouseOverDroppableAreas( axisView!==otherAxis );
            //     } else if( otherAxis ) {
            //         otherAxis.mouseOverDroppableAreas( draggedAttribute, isExtraDropRect, event, axisView!==otherAxis );
            //     }
            // });
        }
		return false;
    }

	// if it leaves without dropping, set the border
	handleMouseOutAxis( event ) {
		"use strict";
		// console.log('plotView.handleMouseOutAxis');
        // Check the current axis and see if style needs to be updated
        var plotView = Dyn.plotView,
            dataSet = plotView.dataSet,
            dragData = DragDropManager.getDraggedValue(),
            dragObject = dragData && JSON.parse( dragData ),
            draggedAttribute = dragObject && dragObject.value && dataSet.getAttributeByID( dragObject.value ),
            isExtraDropRect = this.data('isExtraDropRect' ),
            axisID = event && event.target.getAttribute('axisID'),
            axisView = plotView.getAxisByID( axisID );
        Dyn.assert( draggedAttribute === null || dragObject.type === 'columnName' );

        if( draggedAttribute ) {
            axisView.mouseOutDroppableAreas( draggedAttribute, isExtraDropRect, event );
        }
		return false;
    }

	handleMouseUpOverAxis( event ) {
		"use strict";
		// console.log('plotView.handleMouseUpOverAxis');
        // Check the current axis and see if style needs to be updated
        var plotView = Dyn.plotView,
            dataSet = plotView.dataSet,
            dragData = DragDropManager.getDraggedValue(),
            dragObject = dragData && JSON.parse( dragData ),
            draggedAttribute = dragObject && dragObject.value && dataSet.getAttributeByID( dragObject.value ),
            isExtraDropRect = this.data('isExtraDropRect' ),
            axisID = event && event.target.getAttribute('axisID'),
            axisView = plotView.getAxisByID( axisID );
        Dyn.assert( draggedAttribute === null || dragObject.type === 'columnName' );

		if ( draggedAttribute && dragObject.type === 'columnName' ) {
            axisView.mouseUpDroppableAreas( draggedAttribute, isExtraDropRect, event );
            plotView.dropAttribute( dragObject.value, axisView, isExtraDropRect );
            this.innerHTML = "Dropped " + dragObject.value;
        }
		return false;
    }

    /**
     *  Handle a touchmove initiated by another object, to see if it is over the plot.
     *  Called via DragDropManager.dragInProgress() notification
     *  See also handleMouseOverAxis() for mouse equivalent code
     *  @param data { x, y, dragData, event }
     */
    handleTouchMove( data ) {
        let dropViews = [this.xAxis, this.yAxis, this.y2Axis, this.rightMargin],
            dragObject = data.dragData && JSON.parse( data.dragData ),
            draggedAttribute = dragObject && dragObject.value && this.dataSet.getAttributeByID( dragObject.value ),
            mouseOverData = null;

        dropViews.forEach( (view) => {
            let point = view?.isPointInDropRect( data.x, data.y );
            if( point?.inDropRect ) {
                view.mouseOverDroppableAreas( draggedAttribute, point.inExtraDropRect, data.event );
                mouseOverData = { view:view, isExtraDropRect:point.inExtraDropRect };
            }
        });
        if( mouseOverData?.view !== DragDropManager.overObject?.view ) {
            let outObject = DragDropManager.overObject;
            outObject?.view.mouseOutDroppableAreas( draggedAttribute, outObject.isExtraDropRect, data.event );
        }
        DragDropManager.setOverObject( mouseOverData );
    }

    /**
     * Handle a touchend initiated by another object, to see if it is over the plot.
     * Called via DragDropManager.dragEnd() notification
     * @param data { event, x, y, dragData }
     */
    handleTouchEnd( data ) {
        let dropViews = [this.xAxis, this.yAxis, this.y2Axis, this.rightMargin],
            dragObject = data.dragData && JSON.parse( data.dragData ),
            draggedAttribute = dragObject && dragObject.value && this.dataSet.getAttributeByID( dragObject.value );

        dropViews.forEach( (view) => {
            let point = view?.isPointInDropRect( data.x, data.y );
            if( point?.inDropRect ) {
                if( view === this.rightMargin ) {
                    // To drop attribute on legend area
                    this.rightMargin.mouseUpHandler();
                } else { // drop on X, Y, or Y2
                    view.mouseOutDroppableAreas( draggedAttribute, point.inExtraDropRect, data.event );
                    this.dropAttribute( dragObject.value, view, point.inExtraDropRect );
                }
            }
        });
    }

	//--- plot change commands

	/**
	 * Undo one of our actions.
	 * undo() is passed back the data saved by Dyn.undoManager.saveUndoStep( data, thisPlotView );
     * Note that the caller creates the redo step after this undo, so this function must make sure that
     * undoableData.restoreData already exists or is created.
	 * @param undoableData
	 */
	undo( undoableData ) {
		"use strict";
		var plotElement,
            isUndoRedo = true;

		switch( undoableData.actionID ) {
		case "mixUp":
		case "addElement":
		case "removeElement":
        case "modifyElement":
		case "dropAttribute":
        case "removeAttribute":
        case 'swapAxes':
			undoableData.restoreData = this.save(); // save the current plot state for redo
			this.restore( undoableData.saveData );  // restore the previous state to undo
			break;
        case "dragDivider":
            plotElement = this.plotShaper.findElement( Dyn.EPlotControl.kControlsDividers );
            if( plotElement ) {
                Dyn.assert( undoableData.saveData && undoableData.restoreData ); // from saveUndoStep() in handleDragDivider()
                plotElement.restore( undoableData.saveData ); // restore the pre-move dividers
                this.handleDragDivider( {}, isUndoRedo ); // update the plot view
            }
            break;
        case "updatePlotFunction":
            Dyn.assert( undoableData.saveData && undoableData.saveData.id );
            plotElement = this.plotShaper.findElement( Dyn.EPlotControl.kControlsPlotFunction );
            if( plotElement ) { // save the current state for redo (state===null means that restore deletes the function(s))
                undoableData.restoreData = { id: undoableData.saveData.id, state: plotElement.save() };
            } else {
                undoableData.restoreData = { id: undoableData.saveData.id, state: null };
            }
            this.handleUpdatePlotFunction({  // restore plot and refresh ModelFunctions panel
                isRestore:true, restoreData:undoableData.saveData.state, noUndo:true
            });
            break;
        case 'dragCaseUpdate':
            Dyn.assert( undoableData.saveData && undoableData.restoreData ); // from saveUndoStep() in dragCaseUpdate()
            this.dragCaseUpdate( undoableData.saveData.iconIndex, undoableData.saveData.x, undoableData.saveData.y, isUndoRedo );
            break;
        case 'dragMargin':
            Dyn.assert( undoableData.saveData && undoableData.restoreData ); // from saveUndoStep() in handleDragMargin()
            this.handleDragMargin( undoableData.saveData, isUndoRedo );
            break;
        case 'axisButtonClick':
            Dyn.assert( undoableData.saveData && undoableData.restoreData ); // from saveUndoStep() in handleAxisButtonClick()
            this.handleAxisButtonClick( undoableData.saveData, isUndoRedo );
            break;
        case 'axisButtonDrag':
            Dyn.assert( undoableData.saveData && undoableData.restoreData ); // from saveUndoStep() in handleAxisButtonDrag()
            this.handleAxisButtonDrag( undoableData.saveData, isUndoRedo );
            break;
        case 'axisSetMinMax':
            Dyn.assert( undoableData.saveData && undoableData.restoreData ); // from saveUndoStep() in handleAxisSetMinMax()
            this.handleAxisSetMinMax( undoableData.saveData, isUndoRedo );
            break;
        case 'updatedPlotAdornment':
            Dyn.assert( undoableData.saveData && undoableData.restoreData ); // from saveUndoStep() in handleUpdatePlotFunction()
            this.handleUpdatePlotAdornment( undoableData.saveData, isUndoRedo );
            break;
		default:
			console.log("Unknown undo actionID: "+undoableData.actionID);
		}
		Dyn.assert( undoableData.restoreData );
    }

	/**
	 * Redo one of our actions.
	 * redo() is passed back the data saved by Dyn.undoManager.saveUndoStep( data, thisPlotView );
	 * redo() is responsible for making sure that the redo is undoable also.
	 * @param undoableData
	 */
	redo( undoableData ) {
		"use strict";
        var self = this,
            plotElement,
            isUndoRedo = true;

        function saveUndoOfRedo( undoData, redoData ) {
            // save another undo step since restore does not create an undo step
            Dyn.undoManager.saveUndoStep( {
                saveData: undoData,
                restoreData: redoData,
                actionID: undoableData.actionID,
                actionString: undoableData.actionString,
            }, self, true );
        }

		switch( undoableData.actionID ) {
		case "mixUp":
		case "addElement":
		case "removeElement":
        case "modifyElement":
		case "dropAttribute":
		case "removeAttribute":
        case 'swapAxes':
			this.restore( undoableData.restoreData ); // restore the state for redo
            saveUndoOfRedo( undoableData.saveData, null );
			break;
        case "dragDivider":
            plotElement = this.plotShaper.findElement( Dyn.EPlotControl.kControlsDividers );
            if( plotElement ) {
                Dyn.assert( undoableData.saveData && undoableData.restoreData ); // from saveUndoStep() in handleDragDivider()
                plotElement.restore( undoableData.restoreData ); // restore the post-move dividers
                this.handleDragDivider( {}, isUndoRedo ); // update the plot view
                saveUndoOfRedo( undoableData.saveData, undoableData.restoreData );
            }
            break;
        case "updatePlotFunction":
            Dyn.assert( undoableData.restoreData && undoableData.restoreData.state );
            this.handleUpdatePlotFunction({ // update plot, model functions panel, etc.
                    isRestore:true, restoreData: undoableData.restoreData.state, noUndo:true
                });
            saveUndoOfRedo( undoableData.saveData, null );
            break;
        case 'dragCaseUpdate':
            Dyn.assert( undoableData.saveData && undoableData.restoreData ); // from saveUndoStep() in dragCaseUpdate()
            this.dragCaseUpdate( undoableData.restoreData.iconIndex, undoableData.restoreData.x, undoableData.restoreData.y, isUndoRedo );
            saveUndoOfRedo( undoableData.saveData, undoableData.restoreData );
            break;
        case 'dragMargin':
            Dyn.assert( undoableData.saveData && undoableData.restoreData ); // from saveUndoStep() in handleDragMargin()
            this.handleDragMargin( undoableData.restoreData, isUndoRedo );
            saveUndoOfRedo( undoableData.saveData, undoableData.restoreData );
            break;
        case 'axisButtonClick':
            Dyn.assert( undoableData.saveData && undoableData.restoreData ); // from saveUndoStep() in handleAxisButtonDrag()
            this.handleAxisButtonClick( undoableData.restoreData, isUndoRedo );
            saveUndoOfRedo( undoableData.saveData, undoableData.restoreData );
            break;
        case 'axisButtonDrag':
            Dyn.assert( undoableData.saveData && undoableData.restoreData ); // from saveUndoStep() in handleAxisButtonDrag()
            this.handleAxisButtonDrag( undoableData.restoreData, isUndoRedo );
            saveUndoOfRedo( undoableData.saveData, undoableData.restoreData );
            break;
        case 'axisSetMinMax':
            Dyn.assert( undoableData.saveData && undoableData.restoreData ); // from saveUndoStep() in handleAxisSetMinMax()
            this.handleAxisSetMinMax( undoableData.restoreData, isUndoRedo );
            saveUndoOfRedo( undoableData.saveData, undoableData.restoreData );
            break;
        case 'updatedPlotAdornment':
            Dyn.assert( undoableData.saveData && undoableData.restoreData ); // from saveUndoStep() in handleUpdatePlotFunction()
            this.handleUpdatePlotAdornment( undoableData.restoreData, isUndoRedo );
            saveUndoOfRedo( undoableData.saveData, undoableData.restoreData );
            break;
		default:
			console.log("Unknown redo actionID: "+undoableData.actionID);
		}
    }

    hasSavedPlotState() {
        return Boolean( this._savedPlotState );
    }

    /**
     * Restore the saved plot state (which is usually the initial plot state), with undo/redo.
     * The saved plot state can be restored multiple times.
     */
    restoreSavedPlotState() {
        Dyn.assert( this._savedPlotState );
        if( this.hasSavedPlotState() ) {
            console.log("PlotView.restore() is restoring a previously saved plot state");
            Dyn.undoManager.saveUndoStep( {
                saveData: this.save(),
                restoreData: null,
                actionID: 'mixUp', // for undo(), same restore as 'mixUp'
                actionString: Dyn.loc( "Reload the plot" )
            }, this );
            Dyn.plotView.restore( this._savedPlotState );
        }
    }

	/** mixUp(), aka "Reset", animates the icons to new random positions */
	mixUp() {
		"use strict";
		var deferPlotUpdate = true, // call updatePlotView() only once at end
			whatChanged = { action: 'mixUp', iconPosition: true, viewSize:true, wantFastCycles: true },
			noSelectedAttribute = { id: 'id', name: '', object: 'mixup', deferPlotUpdate:deferPlotUpdate, noUndo:true },
            noOrderByAttribute = { id: "", name: "", object: "plot", deferPlotUpdate:deferPlotUpdate, noUndo:true },
			noSelectedCases = { ids: [], object: "plot", disableToggle: true, deferPlotUpdate:deferPlotUpdate, noUndo:true };

		// save plot state for undo, note that dataset state is also saved for undo of clearing cases, filters
        Dyn.undoManager.saveUndoStep({
            saveData: this.save(),
            restoreData: null,
            actionID: whatChanged.action,
            actionString: Dyn.loc("Reset")
        }, this );

		// clear attribute colors/legend (with deferPlotUpdate)
		mediator.publish( "records:attributeNameSelected", noSelectedAttribute );
        // clear custom order by attribute (with deferPlotUpdate)
        mediator.publish( "toolBar:orderByAttribute", noOrderByAttribute );
		// and deselect cases (with deferPlotUpdate)
		mediator.publish( "records:casesSelected", noSelectedCases );
		// clear random sampling if viewing a sample (with deferPlotUpdate)
        this.dataSet.caseSampler.resetRandomSampling( { deferPlotUpdate:true, dontRestorePopulationFilter:true } );
        // and clear filters if any (with deferPlotUpdate)
		this.dataSet.caseFilter.resetAll( deferPlotUpdate );
        // reset certain preferences
        Dyn.preferences.setWantY2Axis( false, "plotView" );
        Dyn.preferences.setPrecision( 0, "plotView" );

		// update plot model
		this.plotShaper.reset( new Dyn.MixupElement( Dyn ), this.topMargin ); // the mixup element replaces all other elements automatically
        this.addOrRemoveY2Axis(); // Y2 axis removed, not used by mixupElement
        // if( this.rightMargin.getAttribute() !== null ) {
        //     this.rightMargin.setSelectedAttributeLegend( null );
        // }
		this.plotShaper.updateAxes( whatChanged, this.getSubviewRef() ); // calls mixupElement.calcAxis() to reset axes

		// update plot view (visible elements) based on whatChanged flags
		this.setDefaultViewMode();
		this.updatePlotView( whatChanged );

		// miscellaneous related resets
        this.topMargin.showHidePlotMenu( this.plotShaper.wantPlotContextMenu() );
        Dyn.toolTip.hide(); // hide the visible tooltip in the plot (only in case of bugginess)
        mediator.publish( "plot:reset", {}); // notify non-plot areas of reset
    }

	/**
     *  addPlot() adds the new plot element and animates the plot into the new position.
	 *  The optional extraPlotElement is used in case of Pie, Bar, Histogram plots to
     *  also add a CountElement or PercentElement to show certain counts at the same time.
     * @param newPlotElement {Dyn.PlotElement}-- new plot element
     * @param [extraPlotElement] {Dyn.PlotElement} -- optional 2nd element to add at the same time
     * @param [noAnimation] {boolean} -- if true, do not animate changes
     */
	addPlot( newPlotElement, extraPlotElement, noAnimation ) {
		"use strict";
		var whatChanged = { action: 'addElement', iconPosition: true, noAnimation: noAnimation };

		// save plot state for undo
		Dyn.undoManager.saveUndoStep( {
            saveData: this.save(),
            restoreData: null,
            actionID: whatChanged.action,
            actionString: Dyn.loc( newPlotElement.getUndoAction({
                action:whatChanged.action, extra:extraPlotElement }))
        }, this );

		// update the plot model
        whatChanged.attributeReset = this.plotShaper.wantAttributeResetOnAddElement( newPlotElement );
		this.plotShaper.addElement( newPlotElement, this.topMargin );

		// 2ndPlotElement has been passed, add it to the view
        // removeChangedMargin = false;
		if( extraPlotElement ) {
			this.plotShaper.addElement( extraPlotElement );
		}

		// check for what needs to be updated
		if( newPlotElement[Dyn.EPlotControl.kControlsShape] ) {
            whatChanged.viewSize = true; // axes and margin changes may adjust the view size
        }
        if( newPlotElement[Dyn.EPlotControl.kControlsMargins] ||
            (extraPlotElement && extraPlotElement[Dyn.EPlotControl.kControlsMargins])) {
			whatChanged.viewSize = true; // axes and margin changes may adjust the view size
            whatChanged.viewLayering = true; // margin changes may require relayering
		}
		if( (! whatChanged.viewSize) && newPlotElement[Dyn.EPlotControl.kControlsAdornmentPositionOnly] ) {
			// the added element only changes adornment position, not icon or view position
			whatChanged.adornmentPositionOnly = true;
		}
        this.topMargin.showHidePlotMenu( this.plotShaper.wantPlotContextMenu() );

        // update the axes
        if( whatChanged.viewSize
            || newPlotElement[Dyn.EPlotControl.kControlsShowSquares] ) { // #1403 calcAxis() handles change to which element shows squares
            this.addOrRemoveY2Axis();
            this.plotShaper.updateAxes( whatChanged, this.getSubviewRef() );
        }

		// update the view mode
		if( this.wantDrawToolMode( newPlotElement )) {
            // Skip draw mode for pasting a copied annotation
            if(!newPlotElement.objects.at(-1).isNonEmpty()) {
			    this.startViewMode( { mode:"draw-tool", plotElement:newPlotElement });
            }
		} else if( this.wantDragMapMode( newPlotElement )) {
            this.startViewMode( { mode:"drag-map", plotElement:newPlotElement });
        } else {
			this.setDefaultViewMode();
		}

		// update plot view (visible elements) based on model and whatChanged flags
		this.updatePlotView( whatChanged );
    }

	/**
	 * removePlot() removes the plot element with the given type and updates the plot view.
	 * @param elementControlID {string} -- Dyn.EPlotControl string of plot element to be removed
	 * @param didHaveMarginControl {boolean} -- option boolean parameter if removed element had margin control
	 */
	removePlot( elementControlID, didHaveMarginControl, optionalUndoString ) {
		"use strict";
		let whatChanged = { action: 'removeElement', iconPosition: true };
			//hadMarginControl = didHaveMarginControl || this.plotShaper.hasElement( Dyn.EPlotControl.kControlsMargins );

		// save plot state for undo
        let elementToRemove = this.plotShaper.findElement( elementControlID ),
            hadMarginControl = didHaveMarginControl ||
                (elementToRemove && elementToRemove[Dyn.EPlotControl.kControlsMargins]),
            actionString = optionalUndoString || // caller provides action string "Undo {{action}}"
                Dyn.loc( "Remove {{action}}", {  // element provides action string "Undo Remove Mean", etc
                    action: elementToRemove?.getUndoAction({
                        action:whatChanged.action }) || "" });
		Dyn.undoManager.saveUndoStep({
            saveData:this.save(),
            restoreData:null,
            actionID:whatChanged.action,
            actionString: actionString}, this );

		// update the plot model
		let removedControlFlags = this.plotShaper.removeElement( elementControlID );
		if( hadMarginControl ) {
			whatChanged.viewSize = true; // update view size/axes again after margin change
            whatChanged.viewLayering = true; // margin changes may require re-layering
		}
		if( removedControlFlags && removedControlFlags[Dyn.EPlotControl.kControlsAdornmentPositionOnly] ) {
			// the removed element only changes adornment position, not icon or view position
			whatChanged.adornmentPositionOnly = true;
		}
		this.plotShaper.updateAxes( whatChanged, this.getSubviewRef() );

		// update plot view (visible elements) based on model and whatChanged flags
        this.topMargin.showHidePlotMenu( this.plotShaper.wantPlotContextMenu() );
		this.updatePlotView( whatChanged );
    }

	/**
	 * Modify a control in a plot element, and remove it entirely if all options turned off.
	 * @param primaryControlID {string} -- Dyn.EPlotControl string of plot element to be modified
	 * @param toggleControlID {string} -- Dyn.EPlotControl string of boolean parameter to be modified in element.
     * @param [menuItemName] {string} -- optional name for undo redo
     * @param [noAnimation] {boolean} -- optional, if true, do not animate the plot change
     */
	togglePlotElement( primaryControlID, toggleControlID, menuItemName, noAnimation ) {
		// find and update the element, plot model, then the plot view
		var plotElem = this.plotShaper.findElement( primaryControlID ),
			hadMarginControl = plotElem && plotElem[Dyn.EPlotControl.kControlsMargins],
			whatChanged = { action: 'modifyElement', iconPosition: true, noAnimation:noAnimation },
            toggleSaveData = this.save(),
            isOn = Boolean( plotElem && plotElem[toggleControlID] );

        Dyn.assert( plotElem, "togglePlotElement() could not find "+primaryControlID );
        if( plotElem ) {
            this.plotShaper.toggleElementControl( primaryControlID, toggleControlID );

            if ( plotElem && plotElem.isElementWithAllTogglesOff() ) {
                // remove the element if we turned off everything in the element
                //  NOTE: removed handles the saveUndoStep()
                this.removePlot( primaryControlID, hadMarginControl );
            } else {
                // save plot state for undo
                let actionName = menuItemName ? menuItemName :
                        plotElem.getUndoAction( { action:whatChanged.action, isOn:isOn }),
                    actionString = Dyn.loc( isOn ? "Remove {{action}}": "{{action}}", {
                        action:actionName });
                Dyn.undoManager.saveUndoStep( {
                    saveData: toggleSaveData,
                    restoreData: null,
                    actionID: whatChanged.action,
                    actionString: actionString
                }, this );

                // update the plot
                if ( hadMarginControl || plotElem[Dyn.EPlotControl.kControlsMargins] ) {
                    // the modified element had margin control before or after toggle
                    whatChanged.viewSize = true; // adjust view size again after margin change
                }
                else if ( plotElem[Dyn.EPlotControl.kControlsAdornmentPositionOnly] ) {
                    // the modified element only changes adornment position, not icon or view position
                    whatChanged.adornmentPositionOnly = true;
                }
                this.plotShaper.updateAxes( whatChanged, this.getSubviewRef() );
                this.updatePlotView( whatChanged );
            }
        }
    }

	/**
	 * dropAttribute() -- handle an attribute drop on a plot axis.
	 * @param columnId {string} what was dropped
	 * @param axisView {Dyn.AxisView} which axis
	 * @param isExtraDropRect {boolean} true if dropped in "extra" drop rect used for multiple attributes.
	 */
	dropAttribute( columnId, axisView, isExtraDropRect ){
		"use strict";

        function wantImmediateProgressIndicator( plotView, newAttribute ) {
            // dropping attributes with may categories take time, so we need to show the progress indicator immediately.
            // here we quickly estimate whether drop will take time, and start the progress early.
            // otherwise the progress indicator is only started later.
            const kEnoughCategoriesToSlowUpdate = 100; // empirically derived, change as needed for quick response
            var isCategorical = ((newAttribute.getDataType() === Dyn.dataUtil.types.kCategorical ) &&
                     (newAttribute.stats && newAttribute.stats.categories.length > 0)),
                numCategories = isCategorical ? newAttribute.stats.categories.length : 0;
            return( numCategories >= kEnoughCategoriesToSlowUpdate );
        }
        function dropAttributeTask() {
            //Dyn.time( 'plotView:dropAttributeTask', 'Start' );

            // save plot state for undo
            Dyn.undoManager.saveUndoStep( {
                saveData: plotView.save(),
                restoreData: null,
                actionID: "dropAttribute",
                actionString: Dyn.loc("Drop {{name}}", { "name":newAttribute.name })
            }, plotView );

            // update the plot model
            let whatChanged = axisView.dropAttribute( newAttribute, isExtraDropRect ),
                newPlotElement = plotView.plotShaper.getNewPlotElementForAxes( plotView.xAxis, plotView.yAxis ),
                tasksAlreadyStarted = runAsQueuedTask; // if this task was queued, then tell updatePlotView not to start progress again
            if( newPlotElement ) {
                plotView.plotShaper.addElement( newPlotElement, plotView.topMargin );
            }
            // Update the axes every time an attribute is dropped
            plotView.addOrRemoveY2Axis();
            plotView.plotShaper.updateAxes( whatChanged, plotView.getSubviewRef() );

            // update plot view (visible elements) based on model and whatChanged flags
            plotView.setDefaultViewMode();
            plotView.updatePlotView( whatChanged, tasksAlreadyStarted );

            //Dyn.time( 'plotView:dropAttributeTask', 'End' );

            let moreToDo = false; // run just once
            return ! moreToDo;
        }

		Dyn.assert( [this.xAxis, this.yAxis, this.y2Axis].includes(axisView), "invalid axis" );
		var plotView = this,
            newAttribute = this.dataSet.getAttributeByID( columnId ),
			// immediateProgress = wantImmediateProgressIndicator( this, newAttribute ),
			immediateProgress = true,
            numCases = this.dataSet.getNumCasesPreFilter(),
            runAsQueuedTask = true;

		// set up the progress indicator, show only if "immediateProgress" wanted
		this.progressIndicator.start( this.getProgressIndicatorPoint(), immediateProgress, numCases );

		// do all tasks in the queue, to give time for progress indicator to show
		if( ! this.progressIndicator.queueTask( dropAttributeTask )) {
            console.log("dropAttribute: run dropAttributeTask now");
            runAsQueuedTask = false;
            dropAttributeTask();
        }
    }

	/**
	 * removeAttribute() -- removes the attribute from the given axis
	 * @param axisView --x or y axis (Dyn.AxisView)
	 * @param optionalAttributeIndex
	 */
	removeAttribute( axisView, optionalAttributeIndex ) {
		"use strict";
		Dyn.assert( [this.xAxis, this.yAxis, this.y2Axis].includes(axisView), "invalid axis" );
		var whatChanged, newPlotElement;

		// save plot state for undo
		Dyn.undoManager.saveUndoStep( {
            saveData: this.save(),
            restoreData: null,
            actionID: "removeAttribute",
            actionString: Dyn.loc("Remove Attribute") }, this );

		// update the plot model
		whatChanged = axisView.removeAttribute( undefined, undefined, optionalAttributeIndex );
		newPlotElement = this.plotShaper.getNewPlotElementForAxes( this.xAxis, this.yAxis );
		if( newPlotElement ) {
			this.plotShaper.addElement( newPlotElement, this.topMargin );
		}
		// Update the axes every time an attribute is removed
        this.addOrRemoveY2Axis();
		this.plotShaper.updateAxes( whatChanged, this.getSubviewRef() );

		// update plot view (visible elements) based on model and whatChanged flags
		this.setDefaultViewMode();
		this.updatePlotView( whatChanged );
    }

	/**
	 * From the filter panel, attribute datatype is updated
	 * if attribute is currently on the axis or legend area
	 * make the necessary update
	 */
	// handleAttributeDataTypeUpdate( attribute ) {
	// 	"use strict";
	// 	var thisPlot = Dyn.plotView,
	// 		whatChanged = { viewSize: true, dataChanged: true, action: 'toggleAttribute' },
	// 		attributeOnXAxis = false,
	// 		attributeOnYAxis = false;

	// 	// check the attribute if its on xAxis or yAxis
	// 	// if its on axis, drop again and follow the subsequent steps
	// 	$.each( thisPlot.xAxis.allAttributes.attributes, function( index, value ) {
	// 		if ( value === attribute ){
	// 			thisPlot.xAxis.dropAttribute( attribute );
	// 			attributeOnXAxis = true;
	// 		}
	// 	});
	// 	$.each( thisPlot.yAxis.allAttributes.attributes, function( index, value ) {
	// 		if ( value === attribute ){
	// 			thisPlot.yAxis.dropAttribute( attribute );
	// 			attributeOnYAxis = true;
	// 		}
	// 	});
	// 	// Note: we don't have to reset the attribute on the legend,
	// 	// because the data type change also clears the attribute filter, which
	// 	// calls handleCasesFiltered() which calls updateAxes() and updatePlotView().
	// 	// and then if the filter was changed, or the plot view changed, in either case
	// 	// then MarginView.setSelectedAttributeLegend() is called to redraw the legend
	// 	// Possible to-do: avoid the redundant calls to handleCasesFiltered(), if inefficient.

	// 	if ( attributeOnXAxis || attributeOnYAxis ){
	// 		//
	// 		thisPlot.plotShaper.updateAxes( whatChanged, thisPlot.getSubviewRef() );
	// 		// update plot view (visible elements) based on model and whatChanged flags
	// 		thisPlot.updatePlotView( whatChanged );
	// 	}
	// },

    handleUpdateAttributeValues( args ) {
        let plotAttributesUpdate = false,
            caseValuesUpdate = false;
        if( args.attributeIDs ) {
            // update only if the changed attributes are in the plot
            plotAttributesUpdate = Boolean(args.attributeIDs.find( id => this.isAttributeInPlot( id )));
        } else {
            // every attribute changed: update if any attributes are in the plot
            plotAttributesUpdate = this.isAnyAttributeInPlot();
        }
        if( args.addAttribute || args.deleteAttribute ) {
            // if adding or deleting attributes, this causes color changes to other attributes,
            // so update the plot if there is a legend attribute
            plotAttributesUpdate = plotAttributesUpdate || this.dataSet.getSelectedAttribute();
        } else if( args.numCasesUpdate ) {
            caseValuesUpdate = true;
        }
        if( plotAttributesUpdate ){
            // can be optimized, see also handleUpdateAttributeMetadata()
            this.refresh({ action:'updateAllAttributes' });
        } else if( caseValuesUpdate ) {
            let whatChanged = { dataChanged: true, action: 'caseAttributeValueChange' };
            this.plotShaper.updateAxes( whatChanged, this.getSubviewRef() );
            this.updatePlotView( whatChanged );
        }
    }

	handleUpdateAttributeMetadata( args ) {
		"use strict";

		var //thisPlot = Dyn.plotView,
			whatChanged = { viewSize: true, dataChanged: true, action: 'toggleAttribute' },
			attributeOnAxis = false,
			attributeSelected = false;

		// check for updates to the axes and legend if they use the attribute
        if( this.xAxis.isAttributeOnAxis( args.attribute.id )) {
            this.xAxis.droppedAttributesUpdate( args.attribute );
            attributeOnAxis = true;
		}
        if( this.yAxis.isAttributeOnAxis( args.attribute.id )) {
            this.yAxis.droppedAttributesUpdate( args.attribute );
            attributeOnAxis = true;
		}
        if( this.y2Axis && this.y2Axis.isAttributeOnAxis( args.attribute.id )) {
            this.y2Axis.droppedAttributesUpdate( args.attribute );
            attributeOnAxis = true;
        }
        if( (this.dataSet.getSelectedAttribute() === args.attribute) ||
            (this.dataSet.getOrderByAttribute() === args.attribute )) {
            this.rightMargin.handleAttributeUpdate( args.attribute );
			attributeSelected = true;
		}
		// Note: if data type change, caller also clears the attribute filter, which
		// calls handleCasesFiltered() which calls updateAxes() and updatePlotView().
		// Possible to-do: avoid the redundant calls to handleCasesFiltered(), if inefficient.

		if ( attributeOnAxis || attributeSelected ){
			// update axes
            this.plotShaper.updateAxes( whatChanged, this.getSubviewRef() );
			// update plot view (visible elements) based on model and whatChanged flags
            this.updatePlotView( whatChanged );
		}
	}

	/**
	 * Make plot image ready by removing the interaction controls and unused axis.
	 */
	makePlotImageReady( isPreImageCapture ) {
        this.setImageReadyOrViewOnlyMode( new Dyn.PlotModeImageReady( isPreImageCapture ));
    }

    /**
     * update the plot for view-only mode or pre- or post-image capture.
     * Note that these modes are somewhat independent of interaction modes set by
     * startViewMode(), stopViewMode(), setDefaultViewMode().
     * @param mode {{Dyn.PlotViewMode} | {Dyn.PlotModeImageReady}} - new mode settings
     */
    setImageReadyOrViewOnlyMode( mode ) {
	    Dyn.assert( mode && (typeof mode.showControls()==='boolean'), 'not a valid image ready or view only mode');
	    Dyn.assert( Dyn.preferences.isViewOnly() === mode.wantViewOnlyPlot );

	    // show/hide controls in subviews
		this.xAxis.setViewMode( mode );
		this.yAxis.setViewMode( mode );
        this.y2Axis?.setViewMode( mode );
		this.topMargin.setViewMode( mode );
		this.rightMargin.setViewMode( mode );
		this.plotShaper.setViewMode( mode );

		// show/hide local controls
        let showControls = mode.showControls(),
            closeButtonForOrderBy = this.excludedCasesDisplay[5]; // 'X' icon for "Ordered by" setting
        Dyn.plotUtil.showOrHideElement( closeButtonForOrderBy, showControls && this.plotShaper.hasCustomOrderBy() );
        Dyn.plotUtil.showOrHideElement( this.swapAxesButton, showControls && this.wantSwapAxesButton() );

		// clear any unfinished interactions
		this.setDefaultViewMode();
    }

	/**
	 * Swap the X and Y axes on the plot and rebuild.
	 */
	swapAxes() {
		"use strict";
		var savedXAxis = this.xAxis.save(),
			savedYAxis = this.yAxis.save(),
			whatChanged = { viewSize: true, iconPosition: true, action: 'swapAxes' };

        // save plot state for undo
        Dyn.undoManager.saveUndoStep({
            saveData: this.save(),
            restoreData: null,
            actionID: whatChanged.action,
            actionString: Dyn.loc("Swap Axes")
        }, this );

		// update the plot model
        Dyn.plotStrings.swapAxisPrompts( savedXAxis, savedYAxis, 'titleString' );
		this.xAxis.restore( savedYAxis ); // restore Y data to X
		this.yAxis.restore( savedXAxis ); // restore X data to Y
        this.plotShaper.swapAxes(); // handle special cases in shaper
		this.plotShaper.updateAxes( whatChanged, this.getSubviewRef() ); // update shaper to new axis model

		// update plot view (visible elements) based on model and whatChanged flags
		this.updatePlotView( whatChanged );
    }

	/**
	 * updatePlotView() -- update the plot view (those parts that may or may not be animated),
	 * either immediately or with animation, based on 'whatChanged' flags.
     * For large numbers of cases, show progress indicator and update in batches using timed task.
     *
	 * @param whatChanged
	 */
	updatePlotView( whatChanged, tasksAlreadyStarted ) {
		"use strict";
        //console.log("-----------------------------------" );
		//console.log("updatePlotView started with action: "+whatChanged.action /*, whatChanged*/ );
		//Dyn.time( 'plotView:updatePlotView', 'Start' );
		//Dyn.time( 'updatePlotView:calc', 'Start' );
        //Dyn.time( 'progressIndicator', 'Start' );

        function wantImmediateProgressIndicator( plotView, whatChanged, numCasesPreFilter ) {
            // larger data sets take more time, so we need to show the progress indicator immediately.
            // here we quickly estimate the plot size before a lot of lengthy
            // calculations have been made to update the plot.  We err on the side of showing the indicator;
            // for example we don't count the number of filter-out-cases, number of cases hidden because of icon
            // grouping (e.g. 1000 cases but only 3 pie sections because they are in 3 groups).
            var iconsPerCase = plotView.plotShaper.numIconsPerCase,
                newNumCases = numCasesPreFilter,
                oldNumCases = plotView.casePositions.length,
                weAreExpectingManyIcons = Dyn.plotUtil.stdIconsPerCycle <= (iconsPerCase * Math.max( newNumCases, oldNumCases ));
            // certain calculation types are known to be lengthy, indicate them here, and explain why.
            var weAreExpectingComplexUpdateType = Boolean(
                whatChanged.iconPosition    || // slow, queues tasks, which means we want a progress indicator
                whatChanged.iconSelection   || // slow, queues tasks, which means we want a progress indicator
                whatChanged.isInDrag ||        // for marquee select we want to show progress immediately for better feedback
                whatChanged.viewSize           // sets whatChanged.iconPosition, so this is also slow.
                // else  don't show progress indicator immediately, e.g. for fast tasks like update adornment only, update color only
            );  // queues tasks
            //console.log( "PlotView iconsPerCase:" + iconsPerCase + " newNumCases:"+ newNumCases + " oldNumCases:" + oldNumCases );
            return( weAreExpectingComplexUpdateType && weAreExpectingManyIcons );
        }

		var plotView = this,
            animator = this.plotAnimator,
            iconArray = this.icons,
            numCasesPreFilter = this.dataSet.getNumCasesPreFilter(),
			selectedAttribute = this.dataSet.getSelectedAttribute(),
			colorScheme = this.getColorScheme(),
			isLastUpdateUnfinished = this.progressIndicator.isRunning();

        if( ! tasksAlreadyStarted ) {
			// this.progressIndicator.start( this.getProgressIndicatorPoint(), wantImmediateProgressIndicator( this, whatChanged, numCasesPreFilter ), numCasesPreFilter );
            this.progressIndicator.start( this.getProgressIndicatorPoint(), true, numCasesPreFilter );
        }
        //Dyn.time( 'progressIndicator', 'End' );

		// track the number of icons added/updated/swapped for smooth animations even if plot is large
		whatChanged.numIconsAdded = 0;	// e.g. go from 10 to 20 icons
		whatChanged.numIconsUpdated = 0; // e.g. move circle position
		whatChanged.needsIconAdding = true;
		whatChanged.needsIconUpdating = true;
		whatChanged.needsRelayering = false;
        whatChanged.noAnimation = whatChanged.noAnimation || ! Dyn.preferences.isAnimationOn();
		this.plotAnimator.initTimings( whatChanged.noAnimation, whatChanged.wantFastCycles );

		if( whatChanged.adornmentPositionOnly ) {
			// this plot update only changes static (non-animated) adornments, not icons positions.
			// Don't update icons or do animation timing/progress meter, unless previous update not complete.
			whatChanged.iconPosition = isLastUpdateUnfinished;
            whatChanged.needsRelayering = true; // if adornments added they may need layering
		} else if ( whatChanged.viewLayering || whatChanged.attributeReset || whatChanged.iconSelection ) {
            whatChanged.needsRelayering = true; // if axis/attribute/selection changed, we need relayering
        }

        //Dyn.time( 'updateDataSize', 'Start' );
		if( this.plotShaper.updateDataSize( numCasesPreFilter, this.casePositions )){
			// we resized the case positions array
			whatChanged.iconPosition = true;
		}
        //Dyn.time( 'updateDataSize', 'End' );
        //Dyn.time( 'updateIconStyle', 'Start' );
        if( this.plotShaper.updateIconStyle( this.casePositions, this.xAxis, this.yAxis, this.y2Axis, this.rightMargin )){
            // icon size or style changed; axis margins might have changed as a result
            whatChanged.iconSize = true;
        }
        //Dyn.time( 'updateIconStyle', 'End' );
		if( whatChanged.viewSize ) {
            //Dyn.time( 'updatePlotBounds', 'Start' );
			// update view sizes to match changed axis
			this.updatePlotBounds( false /* don't initialize */ );
			if( this.xAxis.showGridLines || this.yAxis.showGridLines ) {
                whatChanged.needsRelayering = true; // new grid lines must be relayed to be under plot interior
            }
			whatChanged.iconPosition = true;
            //Dyn.time( 'updatePlotBounds', 'End' );
		}
		if( whatChanged.iconPosition ) {
            //Dyn.time( 'updatePositionsAndAdornments', 'Start' );
			// calculate the new position of all case icons, and directly position adornment view elements
			this.plotShaper.updatePositionsAndAdornments( this.casePositions, this.adornments, this.getSubviewRef(), animator.noAnimation );
            whatChanged.needsRelayering = whatChanged.needsRelayering || Dyn.plotUtil.enableCaseTabOrder; // to update case tab order
			// determine animation speed/characteristics once we know icon visibility
            animator.setIconTotals( this.icons.length, countVisibleIcons(), this.casePositions.length, countAppearingIcons());
            animator.setTaskAcceleration();
            animator.setTimingsForIconPositionChange( whatChanged.action==='mixUp' );
			this.updateExcludedCasesCount( animator.animationTime );
            //Dyn.time( 'updatePositionsAndAdornments', 'End' );
            //Dyn.time( 'updatePlotView:calc', 'End' );
			// now change the case icons to match the new positions, usually with animation
			if( ! this.progressIndicator.queueTask( updateIconPosition, animator.totalNumIconsToUpdate, animator.taskAcceleration )) {
                //console.log("updatePlotView run updateIconPosition now");
				updateIconPosition(); // or run now
			}
		} else if( whatChanged.iconColor ) {
			// if only changing the icon color but not position, do it more quickly and efficiently
            animator.setTimingsForIconColorChange();
			updateIconColor();
		} else if( whatChanged.iconSelection ) {
		    animator.setIconTotals( this.icons.length, countVisibleIcons(), this.casePositions.length, countAppearingIcons());
		    animator.setTaskAcceleration( !(whatChanged.isInDrag ));
		    animator.setTimingsForIconSelection();
			if( ! this.progressIndicator.queueTask( updateIconSelection, animator.totalNumIconsToUpdate, animator.taskAcceleration )) {
			    //console.log("updatePlotView run updateIconSelection now");
				updateIconSelection(); // or run now
			}
		} else if( whatChanged.adornmentPositionOnly || whatChanged.statPrecision ) {
			// directly position adornment view elements, don't update the icon elements
			this.plotShaper.updatePositionsAndAdornments( this.casePositions, this.adornments, this.getSubviewRef(), animator.noAnimation );
            if( whatChanged.needsRelayering )
                plotView.layerVisuals();
		}
        //console.log("updatePlotView: animation time = "+animator.animationTime );

        //Dyn.time( 'updatePlotView:finish', 'Start' );
        // legend should already be up-to-date
        Dyn.assert( this.rightMargin.getAttribute() === selectedAttribute, "legend attribute not up to date");

		// notify toolbar buttons, so they can change state
		// console.log( 'plotView.updatePlotView: '+whatChanged.action );
		var plotData = {
			plotView: this,
            plotMeasures: this.plotShaper.measures
		};
		mediator.publish( "plot:modelUpdated", plotData );
		if( ! this.progressIndicator.hasTasks() ) {
			// if no tasks are queued we stop the progress, otherwise the queued tasks do this when they are done.
			this.progressIndicator.stop( "by updatePlotView()" );
		}

        if( this.progressIndicator.debugTaskEnd ) {
            console.log("updatePlotView ended for action: " + whatChanged.action
                + " animation time=" + animator.animationTime + "ms"
                + (this.progressIndicator.hasTasks() ? " + task queued" : " done, no task queued"));
        }
        //Dyn.time( 'updatePlotView:finish', 'End' );
		//Dyn.time( 'plotView:updatePlotView', 'End' );

		function updateIconPosition() {
			//Dyn.time( 'updatePlotView:updateIconPosition', 'Start' );

			// do the following in a timing loop, repeatedly until done, return true if not done
			if( whatChanged.needsIconAdding ) {
				// update icons if needed, updateIconArraySize() will clear whatChanged.needsIconAdding when done
				removeOldOutlines();
				plotView.updateIconArraySize( whatChanged, plotView.casePositions, plotView.icons );
			}
			if( whatChanged.needsIconUpdating ) {
				// update icons if needed, updateCaseIcons() will clear whatChanged.needsIconUpdating when done
				plotView.updateCaseIcons( whatChanged, animator.animationTime, animator.animationEasing );
				// show progress
				plotView.progressIndicator.showProgress( whatChanged.numIconsUpdated * Dyn.mathUtil.invertSafely(animator.totalNumIconsToUpdate), whatChanged.numIconsUpdated );
			}

			// if done with above tasks
			if( !(whatChanged.needsIconAdding || whatChanged.needsIconUpdating )) {

				// relayer icons, only if needed, after all icons are added and updated
				if( whatChanged.needsRelayering ) {
					plotView.layerVisuals();
					whatChanged.needsRelayering = false; // only do once
				}
                // clean up and remove invisible icons later, when progress indicator and animations are complete
                plotView.progressIndicator.queueStopFunction( function(){
                    plotView.removeInvisibleIcons();
                }, 'removeInvisibleIcons()');
				plotView.progressIndicator.setStopDelay( animator.animationTime ); // stop progress at same time last icon animation finishes
			}

			//Dyn.time( 'updatePlotView:updateIconPosition', 'End' );
			var moreToDo =( whatChanged.needsIconAdding || whatChanged.needsIconUpdating || whatChanged.needsRelayering );
			return ! moreToDo;
		}
		function updateIconColor() {
            iconArray.forEach( (icon, i) => {
                if( icon ) {
                    let isSelected = icon.data( "isSelected" ),
                        color = plotView.calcCaseColor( plotView.casePositions[i], colorScheme, isSelected );
                    icon.animate( {fill: color.fillColor.colorString, stroke: color.borderColor }, animator.animationTime, animator.animationEasing );
                }
            });
		}
		function updateIconSelection() {

			if( whatChanged.needsIconAdding ) {
				removeOldOutlines();
				whatChanged.needsIconAdding = false;
			}
			if( whatChanged.needsIconUpdating ) {
				// update icons if needed, updateCaseIcons() will clear whatChanged.needsIconUpdating when done
				plotView.updateIconSelection( whatChanged );
				// show progress
				plotView.progressIndicator.showProgress( whatChanged.numIconsUpdated * Dyn.mathUtil.invertSafely(animator.totalNumIconsToUpdate), whatChanged.numIconsUpdated );
			}
			if( whatChanged.needsRelayering && !(whatChanged.needsIconAdding || whatChanged.needsIconUpdating )) {
				plotView.layerVisuals();
				whatChanged.needsRelayering = false; // only do once
			}
			var moreToDo =( whatChanged.needsIconUpdating || whatChanged.needsRelayering );
			return ! moreToDo;
		}
		function removeOldOutlines() {
			// remove unneeded outlines from the previous icon selection
			// for now, we simply delete all outlines, then later add in the current outlines for selected cases.
			var i, element, outlineArray = plotView.outlines;
			for( i=outlineArray.length-1; i >= 0; --i ) {
				element = outlineArray[i];
				element.stop().remove();
			}
			plotView.outlines.length = 0;
		}
        function countVisibleIcons() {
            var visibleIconsCount = 0;
		    plotView.icons.forEach( ( icon ) => {
                if( icon ) {
                    ++visibleIconsCount; // else icon has been hidden from previous update
                }
            });
            return visibleIconsCount;
        }
        function countAppearingIcons() {
            var appearingIconsCount = 0;
		    plotView.casePositions.forEach( (p) => {
		        if( p.isVisible ) {
		            ++appearingIconsCount; // else icon will be hidden by this update
                }
            });
            return appearingIconsCount;
        }
        this.updatePlotSummary();
    }

    updatePlotSummary() {

        var plotView = this,
            summaryArray = [],
            plotSummary = [],
            xAttributes = [],
            yAttributes = [],
            individualPlotSummary = "";
        let mixupElement = plotView.plotShaper.findElement(
            Dyn.EPlotControl.kControlsMixup ),
            mapElement = plotView.plotShaper.findElement(
                Dyn.EPlotControl.kControlsMap),
            pieElement = plotView.plotShaper.findElement(
                Dyn.EPlotControl.kControlsPieChart);

        // plot details
        // Get the plotSummary for the each plot
        $.each( plotView.plotShaper.plotElements, function( index, value ) {
            individualPlotSummary = value.getPlotSummary();

            // we need to skip in case of count, percent and other stats
            if (individualPlotSummary) {
                plotSummary.push(value.getPlotSummary());
            }
        });

        // if current plot is mixup, skip the entire thing -
        if (!mixupElement) {
            plotView.xAxis.allAttributes.attributes.forEach(( attrib, index ) => {
                xAttributes.push(attrib.name);
            });
            plotView.yAxis.allAttributes.attributes.forEach(( attrib, index ) => {
                yAttributes.push(attrib.name);
            });
            // get axis summary
            if( !plotView.xAxis.isFrequencyAxis() && !plotView.xAxis.isPercentAxis()){
                if (xAttributes.length === 0) {
                    plotSummary.push(Dyn.stringFormat(Dyn.plotUtil.noAttributeOnAxis, 'x-axis'));
                } else if (xAttributes.length === 1) {
                    plotView.xAxis.allAttributes.attributes.forEach(( attrib, index ) => {
                        plotSummary.push(Dyn.stringFormat(Dyn.plotUtil.attributeOnAxisSummary,
                            'x-axis',
                            attrib.getDataType(),
                            attrib.name));
                        if (attrib.getDataType() === 'numerical' && plotView.xAxis.isNumericalActiveAxis() && !mapElement && !pieElement){
                            plotSummary.push(Dyn.stringFormat(Dyn.plotUtil.numericalAttributeSummary,
                                'x-axis',
                                plotView.xAxis.numeric.axisRange.min,
                                plotView.xAxis.numeric.axisRange.max,
                                plotView.xAxis.numeric.snapIntervalWidth));
                        } else if (attrib.getDataType() === 'categorical' && plotView.xAxis.isCategoricalAxis() ) {
                            plotSummary.push(Dyn.stringFormat(Dyn.plotUtil.categoricalAttributeSummary,
                                attrib.name,
                                attrib.stats.numCategories(),
                                attrib.stats.categories.join(', ').replace(/, ([^,]*)$/, ' and $1')));
                        }
                    });
                } else if (xAttributes.length > 1) {
                    // it's only possible for numerical attributes
                    plotSummary.push(Dyn.stringFormat(Dyn.plotUtil.multipleNumericAttributeSummary,
                        'x-axis',
                        xAttributes.join(', ').replace(/, ([^,]*)$/, ' and $1')));
                    if (plotView.xAxis.isNumericalActiveAxis()) {
                        plotSummary.push(Dyn.stringFormat(Dyn.plotUtil.numericalAttributeSummary,
                            'x-axis',
                            plotView.xAxis.numeric.axisRange.min,
                            plotView.xAxis.numeric.axisRange.max,
                            plotView.xAxis.numeric.snapIntervalWidth));
                    }
                }
            }
            if (plotView.xAxis.isFrequencyAxis()){
                plotSummary.push(Dyn.stringFormat(Dyn.plotUtil.frequencyAxisSummary,
                    "x-axis"));
            } else if (plotView.xAxis.isPercentAxis()){
                plotSummary.push(Dyn.stringFormat(Dyn.plotUtil.percentageAxisSummary,
                    "x-axis"));
            } else if (plotView.xAxis.isIncompleteAxis()){
                plotSummary.push(Dyn.stringFormat(Dyn.plotUtil.disabledAxisSummary,
                    "x-axis"));
            }

            if( !plotView.yAxis.isFrequencyAxis() && !plotView.yAxis.isPercentAxis()){
                if (plotView.yAxis.allAttributes.attributes.length === 0) {
                    plotSummary.push(Dyn.stringFormat(Dyn.plotUtil.noAttributeOnAxis, 'y-axis'));
                } else if (yAttributes.length === 1) {
                    plotView.yAxis.allAttributes.attributes.forEach(( attrib, index ) => {
                        plotSummary.push(Dyn.stringFormat(Dyn.plotUtil.attributeOnAxisSummary,
                            'y-axis',
                            attrib.getDataType(),
                            attrib.name));
                        if (attrib.getDataType() === 'numerical' && plotView.yAxis.isNumericalActiveAxis() &&!mapElement && !pieElement){
                            plotSummary.push(Dyn.stringFormat(Dyn.plotUtil.numericalAttributeSummary,
                                'y-axis',
                                plotView.yAxis.numeric.axisRange.min,
                                plotView.yAxis.numeric.axisRange.max,
                                plotView.yAxis.numeric.snapIntervalWidth));
                        }  else if (attrib.getDataType() === 'categorical' && plotView.yAxis.isCategoricalAxis() ) {
                            plotSummary.push(Dyn.stringFormat(Dyn.plotUtil.categoricalAttributeSummary,
                                attrib.name,
                                attrib.stats.numCategories(),
                                attrib.stats.categories.join(', ').replace(/, ([^,]*)$/, ' and $1')));
                        }
                    });
                } else if (yAttributes.length > 1) {
                    plotSummary.push(Dyn.stringFormat(Dyn.plotUtil.multipleNumericAttributeSummary,
                        'y-axis',
                        yAttributes.join(', ').replace(/, ([^,]*)$/, ' and $1')));
                    if (plotView.yAxis.isNumericalActiveAxis()) {
                        plotSummary.push(Dyn.stringFormat(Dyn.plotUtil.numericalAttributeSummary,
                            'x-axis',
                            plotView.yAxis.numeric.axisRange.min,
                            plotView.yAxis.numeric.axisRange.max,
                            plotView.yAxis.numeric.snapIntervalWidth));
                    }
                }
            }
            if (plotView.yAxis.isFrequencyAxis()){
                plotSummary.push(Dyn.stringFormat(Dyn.plotUtil.frequencyAxisSummary,
                    "y-axis"));
            } else if (plotView.yAxis.isPercentAxis()){
                plotSummary.push(Dyn.stringFormat(Dyn.plotUtil.percentageAxisSummary,
                    "y-axis"));
            } else if (plotView.yAxis.isIncompleteAxis()){
                plotSummary.push(Dyn.stringFormat(Dyn.plotUtil.disabledAxisSummary,
                    "y-axis"));
            }
        }
        // get legend summary
        if (plotView.rightMargin.attribute !== null){
            plotSummary.push(Dyn.stringFormat(Dyn.plotUtil.legendSummary,
                plotView.rightMargin.attribute.getDataType(),
                plotView.rightMargin.attribute.name));
            if (plotView.rightMargin.attribute.getDataType() === 'numerical'){
                plotSummary.push(Dyn.stringFormat(Dyn.plotUtil.numericalLegendAttributeSummary,
                    plotView.rightMargin.attribute.stats.min,
                    plotView.rightMargin.attribute.stats.max));
            } else {
                plotSummary.push(Dyn.stringFormat(Dyn.plotUtil.categoricalLegendAttributeSummary,
                    plotView.rightMargin.attribute.stats.categories.length,
                    plotView.rightMargin.attribute.stats.categories.length,
                    plotView.rightMargin.attribute.stats.categories.join(', ').replace(/, ([^,]*)$/, ' and $1')));
            }
            if(Dyn.plotView.rightMargin.attribute.stats.missingCaseCount !== 0){
                plotSummary.push(Dyn.stringFormat(Dyn.plotUtil.missingCaseLegendSummary,
                    plotView.rightMargin.attribute.name));
            }
        } else {
            plotSummary.push(Dyn.stringFormat(Dyn.plotUtil.noLegendSummary));
        }

        // missing cases
        if ( Dyn.dataSet.missingCases.getNumCasesMissing() ){
            plotSummary.push(Dyn.stringFormat(Dyn.plotUtil.missingCasesSummary,
                Dyn.dataSet.getNumCasesPreFilter(),
                Dyn.dataSet.missingCases.getNumCasesMissing()));
        }

        var plotSummaryElement = document.getElementById('plotSummary');

        if (plotSummaryElement) {
            plotSummaryElement.textContent = plotSummary.join('\n');
        } else {
            console.error("plotSummary element not found in the DOM. Please review for accessibility support.");
        }
		// this.plotObj[0].setAttribute('aria-label', plotSummary.join('\n'));
    }

    /**
     * Remove invisible, unneeded icons from the plot.
     * Because this significantly speeds up subsequent operations for large datasets where most icons are hidden (e.g. filtered out, etc),
     * by greatly simplifying the DOM (e.g. 100 visible icons is much faster to manipulate than 100 visible icons and 5900 invisible icons).
     *
     * NOTE: this nulls out the icon value in the PlotView.icons, so icons can be Raphael .circle(), .rect(), .path() or null.
     * We do not shorten the array, so that icons keep the 1:n correspondence with cases in the dataset (1:1, 1:2, 1:3, etc).
     * Typically these icons have already been moved (with animation by updatePlotView()) to the excluded cases position or other invisible locations.
     */
    removeInvisibleIcons() {
        "use strict";
        var iconArray = this.icons,
            debugRemoveCount = 0,
            i, j, icon;
        for( i=0, j=iconArray.length; i < j; ++i ) {
            icon = iconArray[i];
            if ( icon && ! icon.data("visible")) {
                icon.stop().remove();
                iconArray[i] = null;
                ++debugRemoveCount;
            }
        }
        if( debugRemoveCount > 0 ) {
            console.log( "PlotView removeInvisibleIcons() " + debugRemoveCount + " removed out of "+this.icons.length );
        }
    }

	/**
	 * Add or remove case icons to/from the paper, and save them in the icons array,
	 * to match the desired number in PlotView.casePositions[].
	 * @param whatChanged
	 * @param casePositions [] - array of objects with {x,y,...} positional data for each case
	 * @param iconArray [] - array of rafael elements to be updated
	 * @returns {Boolean} - true if iconArray has changed.
	 */
	updateIconArraySize( whatChanged, casePositions, iconArray ) {
		"use strict";
		//Dyn.time( 'updateIconArraySize', 'Start' );
		var isInitialLoadOfPlot = ( this.iconStyleUsed === Dyn.EIconStyle.kNoStyle ), // see Dyn.PlotView() constructor
            newNumIcons = this.casePositions.length,
			iconArrayChange =( iconArray.length !== newNumIcons ),
			iconStyler = this.plotShaper.iconStyler,
			//currIconStyle = this.iconStyleUsed,
			nextIconStyle = iconStyler.style,
			caseColor = { fillColor: { colorString: Dyn.plotUtil.kCaseColor}, borderColor: Dyn.plotUtil.kCaseBorderColor },
			initialPosition = {}, // hold case-invariant parameters set by createInitialCasePositionData()
			i, finalPosition;

		// add any needed icons and save them in the returned array.
		for (i=iconArray.length; i < newNumIcons; i++) {
			// Create icons in an initial position, assuming that the
			// caller will then animate to the final position/shape via updateCaseIcons().
			finalPosition = casePositions[i];
			iconStyler.createInitialCasePositionData( initialPosition, finalPosition, isInitialLoadOfPlot, this.plotAnimator.noAnimation );
			if( isInitialLoadOfPlot ) {
				// if creating icons for the first time (initial load), position them randomly in plot
				initialPosition.x = this.xAxis.dataToPixel( Math.random());
				initialPosition.y = this.yAxis.dataToPixel( Math.random());
			}
			iconArray.push( this.makeOneCaseIcon( initialPosition, nextIconStyle, caseColor, i ));
			whatChanged.numIconsAdded++;
		}
		if( isInitialLoadOfPlot ) {
			// if plot is being initialized, record the icon style used, for updateCaseIcons() animated transitions.
			this.iconStyleUsed = nextIconStyle;
		}

		// remove any unneeded icons
		for (i=iconArray.length; i > newNumIcons; --i) {
			var oldIcon = iconArray.pop(); // remove from array
            if( oldIcon ) {
                oldIcon.remove(); // remove from Rafael paper
                // Note: we could do a fancy fade-away animation and remove on animation end.
                //console.log( "plotView.updateIconArraySize() removing icon "+i );
            }
		}

		if( iconArray.length === this.casePositions.length ) {
			whatChanged.needsIconAdding = false;
		}
		Dyn.assert( iconArray.length === newNumIcons );
		//Dyn.time( 'updateIconArraySize', 'End' );
		if( iconArrayChange ) { // we added/removed plot elements, instead of just repositioning, so elements need to be relayered
			whatChanged.needsRelayering = true;
		}
    }

	/**
	 * Update the case icons, animating them from the current positions to the new,
	 * changing the icon style if needed.
	 * @param whatChanged
	 * @param animationTime
	 * @param animationEasing
	 */
	updateCaseIcons( whatChanged, animationTime, animationEasing ) {
        //Dyn.time( 'updateCaseIcons', 'Start' );
		const clipToMap = this.plotShaper.hasElement( Dyn.EPlotControl.kControlsMap );
		let iconArray = this.icons,
			iconStyler = this.plotShaper.iconStyler,
			colorScheme = this.getColorScheme(),
			currIconStyle = this.iconStyleUsed,
			nextIconStyle = iconStyler.getNextStyleForAnimations( this.iconStyleUsed ),
			targetIconStyle = iconStyler.style,
            //needNewIcon = false,
			needReLayering = false,
			clipRectString = this.getPlotInteriorClipRect( 0, 0, 0, 0, clipToMap ),
			mvClipRectString = this.getPlotInteriorClipRect(
                Dyn.plotUtil.missingCasePosX, Dyn.plotUtil.missingCasePosY ),
			//i, j, p, icon, isSelected, fillColor, borderColor, outline,
			animation1 = null,
			element1 = null,
            isSynchronized = true;

        //console.log( "updateCaseIcons() updating "+i+" to "+j+" with animation time="+animationTime+" ms");

		// update another batch of icons
        for(
            let i = whatChanged.numIconsUpdated,  // 0+, start after last updated icon
		        j = Math.min( iconArray.length, i + this.progressIndicator.getIconsPerCycle() );
            i < j; i++ ) {

            let p           = this.casePositions[i],
                icon        = iconArray[i],
                isSelected  = this.dataSet.isCaseSelected( p.caseIndex ),
                caseColor   = this.calcCaseColor( p, colorScheme, isSelected ),
                needNewIcon = ( ( targetIconStyle !== currIconStyle ) ||
                            ((icon === null) ? p.isVisible : ( p.caseIndex !== icon.data('caseIndex'))));
            let outlineIndex = -1;

            // calc p.new always
            iconStyler.calcNewCasePositionData( p, caseColor.fillColor, caseColor.borderColor, isSelected, currIconStyle, nextIconStyle );

            // stop any previous animations that may interfere with current animation
            //		Note: this assumes that previous animations do not need to be completed;
            //		we need to be careful that all icon parameters that animate are re-created here
            if ( icon ) icon.stop();

            // calc p.old if needed to replace or create new icon, or add new selection outline
            if ( isSelected || needNewIcon ) {
                iconStyler.extractOldCasePositionData( icon, currIconStyle, nextIconStyle, p );
            }

            // Replace old icon, or create new icon, with new type (circle/rect/path) in the OLD position
            if ( needNewIcon ) {
                if ( icon ) icon.remove(); // remove old icon from paper
                icon           = this.makeOneCaseIcon( p.old, nextIconStyle, caseColor, i );
                iconArray[i]   = icon;
                needReLayering = true; // relayer all elements when icons are removed/added
            }

            if ( icon ) {
                // animate the icon to the NEW position/color
                animateIcon( icon, p.new, isSynchronized, animationTime, animationEasing, i );

                // if a selected case, create an animation of the case selection outline
                if( isSelected ) {
                    let outline = this.makeOneCaseOutline( p.old, nextIconStyle, caseColor );
                    outlineIndex = this.outlines.length;
                    this.outlines.push( outline );
                    p.new.fill = "none";
                    p.new.stroke = iconStyler.getOutlineColor( caseColor );
                    p.new['stroke-width'] = iconStyler.getOutlineWidth( targetIconStyle );
                    animateIcon( outline, p.new, isSynchronized, animationTime, animationEasing );
                    outline.attr( 'clip-rect', ( p.isVisible ? clipRectString : mvClipRectString ));
                    needReLayering = true; // if we added outlines, then we need to relayer to put them in the correct z-order
                }

                // clip to plot interior; use expanded rect for icons in missing value area
                icon.attr( 'clip-rect', ( p.isVisible ? clipRectString : mvClipRectString ) );
                icon.data( "isSelected", isSelected );
                icon.data( "outlineIndex", outlineIndex )
                icon.data( "visible", p.isVisible ); // for removeInvisibleIcons() to greatly speed up changes for large dataset with many filtered cases

                // make icons focusable
                icon.node.setAttribute("focusable", p.isVisible ? "true" : "false" );
                icon.node.setAttribute("tabindex",  p.isVisible ? "0" : "-1" );

				Dyn.assert( icon.data( 'caseIndex' ) === p.caseIndex, "Need to update caseIndex of icons" );
            }
            if( !( icon && isSelected)) {
                p.outlineIndex = -1;
            }

			// clean up this.casePositions because it gets re-used
			if( p.old ) {
				iconStyler.removeOldCasePositionData( p, currIconStyle );
			}
			if( ! p.isVisible ) {
                iconStyler.saveLastPostionDataForInvisibleIcon( p );
            }

			whatChanged.numIconsUpdated++;
		}

		if( whatChanged.numIconsUpdated >= iconArray.length ) {
			whatChanged.needsIconUpdating = false;
			this.iconStyleUsed = nextIconStyle; // record the style used by the iconArray[] elements
		}
		if( needReLayering ) { // we added/removed plot elements, instead of just repositioning, so elements need to be relayered
			whatChanged.needsRelayering = true;
		}
		//Dyn.time( 'updateCaseIcons', 'End' );

		/**
		 * Animate a case icon or the outline of a case icon.
         * Parameters referenced/modified by closure:
         *  animation1 -- the animation object to use if synchronized (created on first call)
         *  element1 -- the element to synchronize with (created on first call)
		 * @param element -- Raphael element to animate (circle, rect, or path)
		 * @param attributes -- attributes to animate
         * @param isSynchronized -- if true, use Raphael.animation to keep in perfect sychronization with other elements
		 * @param time -- animation time, or no animation if zero
		 * @param easing -- animation easing
		 */
		function animateIcon( element, attributes, isSynchronized, time, easing ) {
			if( time ) {
				if( ! isSynchronized ) {
					element.animate( attributes, time, easing ); // async animation, later icons have a little bit of delay
				} else if( ! animation1 ) {
					// setup animation object with first icon
					animation1 = Raphael.animation( attributes, time, easing );
					element1 = element.animate( animation1, attributes, time, easing );
				} else {
					// use animateWith to coordinate animations
					Dyn.assert( element1 && animation1 );
					element.animateWith( element1, animation1, attributes, time, easing );
				}
			} else {
				element.attr( attributes ); // animation length == 0, just update the parameters without animation
			}
		}
    }

	/**
	 * Make a Rafael element for the case icon.
	 * Used to create a icon in a new position (no animation), or to create an icon
	 * in an old position to allow caller to animate to a new position.
	 * @param position {*} - view position data for the given icon style (x,y,etc)
	 * @param iconStyle {Dyn.EIconStyle} - style of icon to create
	 * @param caseColor - Dyn.colorUtil fill color, borderColor
   * @param iconIndex
	 * @returns {*}
	 */
	makeOneCaseIcon( position, iconStyle, caseColor, iconIndex ) {
		"use strict";
		Dyn.assert( Number.isFinite( position.caseIndex ));
		let iconStyler = this.plotShaper.iconStyler,
			caseIndex = position.caseIndex,
			isSelected = this.dataSet.isCaseSelected( caseIndex ),
			newIcon,
			pathString;

		Dyn.assert( caseIndex <= this.dataSet.getNumCasesPreFilter() && caseIndex >= 0, "invalid case Index" );

		switch ( iconStyle ) {
		case Dyn.EIconStyle.kCircle:
			Dyn.assertValidCircle( position );
            newIcon = this.paper.circle( position.x, position.y, position.r );
			break;
		case Dyn.EIconStyle.kCirclePath:
			if( position.path ) {
				pathString = position.path;
			} else if( isFinite( position.width )) {
				Dyn.assertValidRect( position );
				pathString = iconStyler.calcRectanglePath( position.x, position.y, position.width, position.height );
			} else {
				Dyn.assertValidCircle( position );
				pathString = iconStyler.calcCirclePath( position.x, position.y, position.r );
			}
			newIcon = this.paper.path( pathString );
			break;
		case Dyn.EIconStyle.kRect:
			Dyn.assertValidRect( position );
			newIcon = this.paper.rect( position.x, position.y, position.width, position.height );
			break;
		case Dyn.EIconStyle.kRectPath:
			if( position.path ) {
				pathString = position.path;
			} else {
				Dyn.assertValidRect( position );
				pathString = iconStyler.calcRectanglePath( position.x, position.y, position.width, position.height );
			}
			newIcon = this.paper.path( pathString );
			break;
		case Dyn.EIconStyle.kSlice:
			if( position.path ) {
				pathString = position.path;
			} else if ( isFinite( position.arcLength )) {
				pathString = iconStyler.calcPieSliceFromPosition( position );
			} else if( isFinite( position.width )) {
				Dyn.assertValidRect( position );
				pathString = iconStyler.calcRectanglePath( position.x, position.y, position.width, position.height );
			} else {
				Dyn.assertValidCircle( position );
				pathString = iconStyler.calcCirclePath( position.x, position.y, position.r );
			}
			newIcon = this.paper.path( pathString );
			break;
        case Dyn.EIconStyle.kCustomIcon:
            if( position.path ) {
                pathString = position.path;
            } else {
                pathString = iconStyler.calcCustomIconPath( position.x, position.y, position.r );
            }
            newIcon = this.paper.path( pathString );
            break;
        case Dyn.EIconStyle.kVariableIcon:
            if( position.path ) {
                pathString = position.path;
            } else {
                pathString = iconStyler.calcVariableIconPath( position.x, position.y, position.r, iconStyler.getVariableIconIndex( position ) );
            }
            newIcon = this.paper.path( pathString );
            break;
		}

		newIcon.attr( {
				fill: caseColor.fillColor.colorString,
				stroke: caseColor.borderColor,
				'stroke-width': iconStyler.getBorderWidth( isSelected )
			});
        newIcon.data( 'caseIndex', caseIndex );
        newIcon.data( 'iconIndex', iconIndex ); // if multiple attributes on an axis, not the same as 'caseIndex'
        PlotViewIcons.makeCaseIconHandlers( Dyn, this, newIcon );

		return newIcon;
    }

	/** make a Raphael element to outline a case icon at the given position */
	makeOneCaseOutline( position, iconStyle, caseColor ) {
		let iconStyler = this.plotShaper.iconStyler,
			caseIndex = position.caseIndex,
			outline,
			circ, rect,
			pathString;

		Dyn.assert( caseIndex <= this.dataSet.getNumCasesPreFilter() && caseIndex >= 0, "invalid case Index" );

		switch ( iconStyle ) {
		case Dyn.EIconStyle.kCircle:
			Dyn.assertValidCircle( position );
			circ = iconStyler.getOutlineCircle( position );
			outline = this.paper.circle( circ.x, circ.y, circ.r );
			break;
		case Dyn.EIconStyle.kCirclePath:
			if( position.path ) {
				pathString = position.path;
			} else if( isFinite( position.width )) {
				Dyn.assertValidRect( position );
				pathString = iconStyler.calcRectanglePath( position.x, position.y, position.width, position.height );
			} else {
				Dyn.assertValidCircle( position );
				circ = iconStyler.getOutlineCircle( position );
				pathString = iconStyler.calcCirclePath( circ.x, circ.y, circ.r );
			}
			outline = this.paper.path( pathString );
			break;
		case Dyn.EIconStyle.kRect:
			Dyn.assertValidRect( position );
			rect = iconStyler.getOutlineRect( position );
			outline = this.paper.rect( rect.x, rect.y, rect.width, rect.height );
			break;
		case Dyn.EIconStyle.kRectPath:
			if( position.path ) {
				pathString = position.path;
			} else {
				Dyn.assertValidRect( position );
				rect = iconStyler.getOutlineRect( position );
				pathString = iconStyler.calcRectanglePath( rect.x, rect.y, rect.width, rect.height );
			}
			outline = this.paper.path( pathString );
			break;
		case Dyn.EIconStyle.kSlice:
			if( position.path ) {
				pathString = position.path;
			} else if ( isFinite( position.arcLength )) {
				pathString = iconStyler.calcPieSliceFromPosition( position );
			} else if( isFinite( position.width )) {
				Dyn.assertValidRect( position );
				pathString = iconStyler.calcRectanglePath( position.x, position.y, position.width, position.height );
			} else {
				Dyn.assertValidCircle( position );
				pathString = iconStyler.calcCirclePath( position.x, position.y, position.r );
			}
			outline = this.paper.path( pathString );
			break;
        case Dyn.EIconStyle.kCustomIcon:
            if( position.path ) {
                pathString = position.path;
                //console.log("makeOneCaseOutline kCustomIcon with path", caseIndex);
            //} else if( isFinite( position.width )) { // #657 don't make rect path here, bar to dot plot transition is wrong
            } else {
                Dyn.assertValidCircle( position );
                circ       = iconStyler.getOutlineCircle( position );
                pathString = iconStyler.calcCustomIconPath( circ.x, circ.y, circ.r );
                //console.log("makeOneCaseOutline kCustomIcon with circle", caseIndex);
            }
            outline = this.paper.path( pathString );
            break;
        case Dyn.EIconStyle.kVariableIcon:
            if( position.path ) {
                pathString = position.path;
                //console.log("makeOneCaseOutline kVariableIcon with path", caseIndex);
            } else {
                Dyn.assertValidCircle( position );
                circ       = iconStyler.getOutlineCircle( position );
                pathString = iconStyler.calcVariableIconPath( circ.x, circ.y, circ.r, iconStyler.getVariableIconIndex( position ));
                //console.log("makeOneCaseOutline kCustomIcon with circle", caseIndex);
            }
            outline = this.paper.path( pathString );
            break;
		}

		outline.attr( {
			fill: "none",
			stroke: iconStyler.getOutlineColor( caseColor ),
			'stroke-width': iconStyler.getOutlineWidth( iconStyle )
		});
		outline.data( 'caseIndex', caseIndex );
		outline.data( 'xAttrIndex', position.xAttrIndex || 0 );
		outline.data( 'yAttrIndex', position.yAttrIndex || 0 );
		outline.toBack();
		return outline;
    }

    /**
     * Update the given case icon and associated selection outline, glow, and tooltip,
     * When dragged by user.
     */
    dragCaseUpdate( iconIndex, newX, newY, isUndoRedo ) {
	    let mixupElement = this.plotShaper.findElement( Dyn.EPlotControl.kControlsMixup ),
            isValidIcon = (iconIndex >= 0) && (iconIndex < this.icons.length);

        if( mixupElement && isValidIcon ) {

	        let iconElement = this.icons[ iconIndex ],
                //caseIndex = iconElement.data("caseIndex"),
                outlineIndex = iconElement.data("isSelected") ? iconElement.data("outlineIndex") : -1,
                viewX = isUndoRedo ? this.xAxis.dataToPixel( newX ) : newX,
                viewY = isUndoRedo ? this.yAxis.dataToPixel( newY ) : newY,
                dataX = isUndoRedo ? newX : this.xAxis.pixelToData( newX ),
                dataY = isUndoRedo ? newY : this.yAxis.pixelToData( newY );
	        Dyn.assert( iconIndex === iconElement.data("iconIndex") && iconIndex >= 0 );

            // move case icon and selection outline (if any)
            iconElement.attr( {cx: viewX, cy: viewY} ); // move case icon
            if( outlineIndex >= 0 && outlineIndex < this.outlines.length ) {
                let outlineElement = this.outlines[outlineIndex];
                outlineElement.attr( {cx: viewX, cy: viewY} );
            }
            // update view-independent random position, so next plot update is correct
            let oldPositions = mixupElement.updateOnePosition( iconIndex, dataX, dataY );

            // create an undo step or update last step if same icon dragged
            if( oldPositions && ! isUndoRedo ) {
                Dyn.undoManager.saveOrUpdateLastUndoStep(
                    {
                        saveData:    { iconIndex:iconIndex, x:oldPositions.x, y:oldPositions.y },
                        restoreData: { iconIndex:iconIndex, x:dataX, y:dataY },
                        actionID: 'dragCaseUpdate',
                        actionString: Dyn.loc( "Drag Case" ),
                    },
                    this,
                    // update the last undo step only if dragging the same icon
                    ( lastUndoData, newUndoData ) => {
                        return( lastUndoData.restoreData.iconIndex === newUndoData.restoreData.iconIndex);
                    }
                );
            }
        }
    }

	/**
	 * Update the visual look of the icons after case selection has changed.
	 * Designed to be called repeatedly by updatePlotView() and Dyn.ProgressIndicator
	 * so that marquee-select of a large dataset is uninterrupted.
	 * @param whatChanged
	 */
	updateIconSelection( whatChanged ) {
		"use strict";
		// update selection status of another batch of icons
		var iconArray = this.icons,
			colorScheme = this.getColorScheme(),
			iconStyler = this.plotShaper.iconStyler,
			targetIconStyle = iconStyler.style,
			i = whatChanged.numIconsUpdated,  // 0+, start after last updated icon
			j = Math.min( iconArray.length, i + this.progressIndicator.getIconsPerCycle() ),
			icon, isSelected, fillColor, borderColor, outline, p, iconAnimation;

		//console.log( "updateIconSelection() updating "+i+" to "+j+" with animation time="+Dyn.plotUtil.quickAnimationTimeMs+" ms");
		for ( ; i < j; i++) {
			let icon = iconArray[i];
			if( icon ) {
                let p = this.casePositions[i],
                    isSelected = this.dataSet.isCaseSelected( icon.data( 'caseIndex' ) ),
                    color  = this.calcCaseColor( p, colorScheme, isSelected );

                // selected case icons get a thicker border
                // also expand radius if Circle/Icon selected, otherwise thickness is interior to Rect/Pie Slice
                switch (targetIconStyle) {
                case Dyn.EIconStyle.kCircle:
                case Dyn.EIconStyle.kCustomIcon:
                case Dyn.EIconStyle.kVariableIcon:
                    iconAnimation = {"r": iconStyler.getRadius( p, isSelected )};
                    break;
                default:
                    iconAnimation = {};
                }
                iconAnimation['stroke-width'] = iconStyler.getBorderWidth( isSelected );
                iconAnimation.stroke          = color.borderColor;
                iconAnimation.fill            = color.fillColor.colorString;
                icon.animate( iconAnimation, this.plotAnimator.animationTime, this.plotAnimator.animationEasing );
                icon.data( "isSelected", isSelected ); // update selection status

                let outlineIndex = -1;
                if ( isSelected ) {
                    // add a selection outline
                    outline = this.makeOneCaseOutline( p, targetIconStyle, color.fillColor );
                    outlineIndex = this.outlines.length;
                    this.outlines.push( outline );
                    whatChanged.needsRelayering = true; // if we added outlines, then we need to relayer to put them in the correct z-order
                    //console.log( "PlotView.updateIconSelection() whatChanged.needsRelayering = true" );
                }
                icon.data("outlineIndex", outlineIndex );
            }
			whatChanged.numIconsUpdated++;
		}
		if( whatChanged.numIconsUpdated >= iconArray.length ) {
			whatChanged.needsIconUpdating = false;
		}
    }

    /** position, update, and show or hide the excluded cases area,
     * which has 6 elements: missing cases count+icon, filtered cases count+icon, Order-by attribute-name+closebox.
     * @param animationTimeMS
     */
	updateExcludedCasesCount( animationTimeMS ) {
		var iconStyler = this.plotShaper.iconStyler,
			numMissing = this.dataSet.missingCases.getNumCasesMissing(),
			numFiltered = this.dataSet.caseFilter.getNumCasesFiltered(),
			oldNumMissing = this.excludedCasesRect.data( "numMissing" ),
			oldNumFiltered = this.excludedCasesRect.data( "numFiltered" ),
            fontSize = Dyn.preferences.getFontSize( Dyn.plotUtil.excludedCasesFontSize ),
			textOffset = fontSize - 0,
			xMiddle = iconStyler.missingValuePosition.x,
            xLeft = xMiddle,
            xRight = xMiddle,
			y = iconStyler.missingValuePosition.y,
            yIcon = y+2, // offset slightly to align with text
			animate = ( animationTimeMS > 0 && ( numMissing !== oldNumMissing || numFiltered !== oldNumFiltered )),
			opacity = ( animate ? 0 : Dyn.plotUtil.excludedCasesTextOpacity ),
            opacityCloseBox = ( animate ? 0 : 1 ),
			numMissingText = this.excludedCasesDisplay[0],
			numFilteredText = this.excludedCasesDisplay[1],
			missingCircle = this.excludedCasesDisplay[2],
			filterIcon = this.excludedCasesDisplay[3],
            orderedByText = this.excludedCasesDisplay[4],
            closeIcon = this.excludedCasesDisplay[5],
			bbox = new Dyn.mathUtil.Rect();

        // show "order by" if ordering by an attribute
        var hasCustomOrderBy = this.plotShaper.hasCustomOrderBy(),
            hasLegend = this.dataSet.getSelectedAttribute() !== null,
            orderByAttribute = this.dataSet.getOrderByAttribute();
        if( Dyn.plotUtil.isTuvaJr() && orderByAttribute===this.dataSet.getSelectedAttribute() ) {
            hasCustomOrderBy = false;
        }

        // Remove the orderbySVGTitle if it is created already
        if(this.orderbySVGTitle) {
            this.orderbySVGTitle.remove();
            this.orderbySVGTitle = null;
        }

        if( hasCustomOrderBy ) {
            orderedByText.data( "tooltip",
                (this.plotShaper.isCustomOrderForLines() ?
                    Dyn.loc( "Lines ordered by {{attributeName}}", { attributeName:orderByAttribute.name }) :
                    Dyn.loc( "Cases ordered by {{attributeName}}", { attributeName:orderByAttribute.name }))
            );
            orderedByText.attr({
                text: Dyn.loc("Ordered by {{attributeName}}", { attributeName:orderByAttribute.name }),
                x: xLeft, y: y, opacity: opacity,
                'text-anchor': "end", 'font-size':fontSize, 'class': 'tde-attribute-name',
                fill: Dyn.plotUtil.titleColor
            }).show();
            bbox = orderedByText.getBBox();
            let xClose = xLeft; // center of Order-by closebox on X
            if( hasLegend ) {
                // Order-by text to right of numFiltered and closebox, centered above legend attribute name
                Dyn.assert(closeIcon.getCoverElement().attr("width")>0);
                xRight += textOffset + 3; /* closeIcon.getCoverElement().attr("width")/2;*/ // move to right of filter symbol
                bbox = Dyn.plotUtil.clipStringToWidth( orderedByText, this.rightMargin.bounds.right() - xRight );
                xRight += (this.rightMargin.bounds.right() - xRight)/2; // to center in legend width
                orderedByText.attr({ x:xRight, "text-anchor":"middle" });
                xClose += 2; // align Order-by close box perfectly with Legend close box
                xLeft -= Dyn.plotUtil.iconSize; // shift filter or missing display to left of close box
            } else {
                // Order-by text anchored at end, with closebox to right of text
                xClose = bbox.x + bbox.width + 15;
                xLeft -= (bbox.width + textOffset );
            }
            xMiddle = xLeft; // middle marks divider between Order-by and Filter display
            closeIcon.getSymbolElement().attr({ opacity: opacityCloseBox });
            // update aria label to provide appropriate information for the current order by attribute context
            closeIcon[0].node.setAttribute("aria-label",
                Dyn.stringFormat(Dyn.plotUtil.removeOrderByButtonAriaLabel, orderByAttribute.name));
            closeIcon.transform( "t"+xClose+","+(yIcon-2));
            Dyn.plotUtil.showOrHideElement( closeIcon, ! Dyn.preferences.isViewOnly())

            // Create svg for mathjax and hide the normal text view
            if(hasMathJaxPattern(orderByAttribute.name)) {
                bbox = orderedByText.getBBox();
                orderedByText.hide();

                this.orderbySVGTitle = this.paper.g();
                this.orderbySVGTitle.node.style.pointerEvents = "bounding-box";
                this.orderbySVGTitle.data( "tooltip", orderedByText.data( "tooltip" ) );
                this.orderbySVGTitle.hover(
                function orderByHoverIn( e ) {
                    Dyn.toolTip.show( this, e, { x:this.attrs.x, y:this.attrs.y, alignRight: true } );
                },
                function buttonHoverOut( e ) {
                    if( Dyn.toolTip.isMyElement( this ))
                        Dyn.toolTip.hide();
                });

                const setSvgTitle = (_svg) => {
                    if(!this.orderbySVGTitle.node) {
                        return;
                    }
                    this.svgCache = {
                        attrname: orderByAttribute.name,
                        svg: _svg
                    }
                    // Style svg
                    _svg.style.fontSize = fontSize;
                    _svg.style.fontWeight = "bold";
                    _svg.style.color = Dyn.plotUtil.titleColor;
                    // Append the svg into the title node
                    this.orderbySVGTitle.node.appendChild(_svg);
                    // To position it in center of the give x and y position
                    this.orderbySVGTitle.attr({
                        transform: 'r0T'+ Math.round(bbox.x) +',' + Math.round(bbox.y),
                        x: Math.round(bbox.x),
                        y: Math.round(bbox.y),
                        width: bbox.width,
                        height: bbox.height
                    });
                }

                // If svg cache is available and the attribute name is same, use the cached svg
                // else create new SVG
                if(this.svgCache && this.svgCache.attrname === orderByAttribute.name && this.svgCache.svg) {
                    setSvgTitle(this.svgCache.svg);
                } else {
                    Dyn.axisUtil.mathJaxTxtToSVG(Dyn.loc("Ordered by {{attributeName}}", { attributeName:orderByAttribute.name }), setSvgTitle);
                }
            }
        } else {
            closeIcon.hide();
            orderedByText.hide();
        }

		// update filter display, position to left of missingValuePosition/legend area
		if( numFiltered > 0 ) {
			filterIcon.attr({ opacity: opacity }).
				transform( "t" + xLeft + "," + yIcon ).
				show();
			xLeft -= textOffset;
			numFilteredText.attr({ text:"-"+numFiltered, x:xLeft, y:y, opacity:opacity, "text-anchor":"end", 'font-size':fontSize } ).
				show();
			bbox = numFilteredText.getBBox();
			xLeft -= (bbox.width + (2 * textOffset ));
		} else {
			filterIcon.hide();
			numFilteredText.hide();
		}

		// update missing display, position to left of numFiltered
		if( numMissing > 0 ) {
			missingCircle.attr({ cx:xLeft, cy:y, opacity: opacity }).
				show();
			xLeft -= textOffset;
			numMissingText.attr({ text:"-"+numMissing, x:xLeft, y:y, opacity:opacity, "text-anchor":"end", 'font-size':fontSize }).
				show();
			bbox = numMissingText.getBBox();
			xLeft -= (bbox.width + textOffset );
		} else {
			missingCircle.hide();
			numMissingText.hide();
		}

		if( animate ) { // animate from opacity zero to final opacity if changed, otherwise update immediately
			$.each( this.excludedCasesDisplay, function( index, value ) {
			    if( value === closeIcon ) {
                    closeIcon.getSymbolElement().animate( {opacity: 1 }, animationTimeMS, Dyn.plotUtil.stdAnimationEasing );
                } else {
                    value.animate( {opacity: Dyn.plotUtil.excludedCasesTextOpacity}, animationTimeMS, Dyn.plotUtil.stdAnimationEasing );
                }
			});
		}

		// update cover rectangle for tooltip over count or excluded cases
        let coverRectWidth = ((xMiddle-xLeft)+textOffset);
		this.excludedCasesRect.attr({ x:xLeft, y:bbox.y, height:bbox.height, width: coverRectWidth });
		this.excludedCasesRect.data( "alignRight", true ); // used by excludedCasesHoverOver()
		this.excludedCasesRect.data( "numMissing", numMissing );
		this.excludedCasesRect.data( "numFiltered", numFiltered );
        if( numMissing > 0 || numFiltered > 0 ) {
            this.excludedCasesRect.show();
        } else {
            this.excludedCasesRect.hide();
        }

		// adjust plot title so it doesn't overlap with excluded cases display
        let titleAndExcludedCasesOverlap = Math.max( 0, this.rightMargin.bounds.left() - xLeft),
            hasExcludedCasesDisplay = ( numMissing > 0 || numFiltered > 0 || hasCustomOrderBy );
        this.topMargin.setExcludedCasesWidth( hasExcludedCasesDisplay ? titleAndExcludedCasesOverlap : 0 );
    }

    wantSwapAxesButton() {
        return !((this.xAxis.isNullAxis() && this.yAxis.isNullAxis()) // something both X and Y axis to swap
            || Dyn.preferences.isViewOnly() // not in view-only mode
            || this.plotShaper.hasElement( Dyn.EPlotControl.kControlsMap )); // not in map plot (scale overlaps)
    }

    /** Position and show/hide the swap axis button which is initialized to 0,0, see init() */
	updateSwapAxesButton() {
		"use strict";
		let showButton = this.wantSwapAxesButton(),
            margin = 4, // to make sure keyboard focus is not clipped at edge of plot
			x = margin + this.yAxis.bounds.left(),
			yIcon  = this.xAxis.bounds.bottom() - ( margin + Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.iconSize )),
            yCover = this.xAxis.bounds.bottom() - ( margin + Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.kMinAxisThickness ));
		if( showButton ) {
			if( ! Dyn.plotUtil.isElementVisible( this.swapAxesButton )) {
				this.swapAxesButton.show();
			}
			this.swapAxesButton.getCoverElement().transform( "t" + x + "," + yCover ).toFront();
            this.swapAxesButton.getSymbolElement().transform( "t" + x + "," + yIcon ).toFront();
		} else if( Dyn.plotUtil.isElementVisible( this.swapAxesButton )) {
			this.swapAxesButton.hide();
		}
    }

	/**
	 * Re-layer all visible elements of the plot, including case icons and adornments and axes.
	 * We bring each element to front, from bottom (z-axis) to topmost layer (z-axis) in order.
	 * Needed when elements are created/destroyed in an unusual order,
	 * or to recover after toFront() is used to make a particular element visible.
	 */
	layerVisuals() {
        //Dyn.time( 'layerVisuals', 'Start' );
        //console.log( "plotView.layerVisuals()" );

        // layer 1: axes tick marks and grid lines (bottom-most in Z-axis)
        this._layerAxes( 0 );

        // layer 2: Case icons
        this._layerCaseIcons( this );

        // layer 3: margins and axes, top to bottom for good keyboard order
        this.topMargin.layerVisuals();
        this.excludedCasesDisplay.forEach( ( v ) => v.toFront() );
        this.excludedCasesRect.toFront(); // covers missing and filtered cases
        this._layerAxes( 1 ); // axis labels and buttons
        this.rightMargin.layerVisuals();
        this.swapAxesButton.toFront();

        // layer 4: Adornments on the top (mean, median, count etc)
        this.plotShaper.layerAdornments();

        // layer 5: misc. upper layer
        if( this.progressIndicator ) {
            this.progressIndicator.toFront();
        }
        this.sonificationRect.toFront();

        // layer 5-topmost: if drawing on the plot, drawModeCoverRect disables interaction with everything underneath
        this.drawModeCoverRect.toFront();
        //Dyn.time( 'layerVisuals', 'End' );
    }

    _layerAxes( layerNumber ) {
        [this.yAxis, this.y2Axis, this.xAxis].forEach( ( a ) => a?.layerVisuals( layerNumber ) );
    }

    _layerCaseIcons() {
        if( Dyn.plotUtil.enableCaseTabOrder ) {
            //  NOTE: use case icon order in each cell, because this affects tab order (with "tabindex" 0),
            //  as well as z-axis layering.  Cell cases usually ordered with Dyn.plotElement.sortCasesInCell()
            //  console.log("layerVisuals ---- ordering cases:");
            let hasSortedCasesInCell = (this.plotShaper.bins.cells.length > 0) && (this.plotShaper.bins.cells[0].cases);
            Dyn.assert( hasSortedCasesInCell );
            // layer A: UN-selected Case Icons to front from first to last
            this.plotShaper.bins.cells.forEach( ( cell ) => {
                cell.cases.forEach( ( iconIndex, ) => {
                    unselectedCaseIconToFront( this.icons[iconIndex], this.casePositions[iconIndex].caseIndex );
                } );
            } );
            // layer B: Selection Outlines to front, below selected icons
            this.outlines.forEach( (v) => v.toFront() );
            // layer C: Selected Case Icons to front from first to last
            this.plotShaper.bins.cells.forEach( ( cell ) => {
                cell.cases.forEach( ( iconIndex ) => {
                    selectedCaseIconToFront( this.icons[iconIndex], this.casePositions[iconIndex].caseIndex );
                } );
            } );
        } else {
            // old way; keep for comparison and optimization tests
            this.icons.forEach( ( caseIcon ) => unselectedCaseIconToFront( caseIcon ));
            this.outlines.forEach( (v) => v.toFront() );
            this.icons.forEach( ( caseIcon ) => selectedCaseIconToFront( caseIcon ));
        }

		function unselectedCaseIconToFront( caseIcon, caseIndex ) {
            if (caseIcon && ! caseIcon.data("isSelected")) {
                //console.log("layerVisuals", caseIndex+1, "unselected");
                caseIcon.toFront();
            }
        }
        function selectedCaseIconToFront( caseIcon, caseIndex ) {
            if (caseIcon && caseIcon.data("isSelected")) {
                //console.log("layerVisuals", caseIndex+1, "selected");
                caseIcon.toFront();
            }
        }
    }

    /** set the keyboard focus to the plot view */
    setFocus() {
        // find and focus the first case icon, if any, as determined by layerCaseIcons()
        let indexCell0Unselected = this.plotShaper.bins.cells[0].cases?.find( i => ! this.icons[i]?.data( "isSelected" ) ),
            indexCell0Selected = this.plotShaper.bins.cells[0].cases?.find( i => this.icons[i]?.data( "isSelected" ) ),
            iconIndex0 = (this.icons.length > 0) ? 0 : undefined, // if no cases in cells (filter/missing values/empty table, etc)
            iconIndexOrNAN = -1 + ((indexCell0Unselected+1) || (indexCell0Selected+1) || (iconIndex0+1)),
            caseIcon = (iconIndexOrNAN >= 0) ? this.icons[iconIndexOrNAN] : null;
        if( caseIcon?.node ) {
            caseIcon.node.focus();
        } else {
            // else if no cases no icons, focus on the plot title (e.g. all missing, all filtered out, or no cases in table yet)
            this.topMargin.setFocus();
        }
    }

	/**
	 * Return a string for clipping an element to the plot interior.
	 * use: element.attr('clip-rect', this.getPlotInteriorClipRect())
	 * @param [expandRight] {number} -Optional amount to expand interior to right
     * @param [expandUp] {number} -Optional amount to expand interior upwards
     * @param [expandLeft] {number} -Optional amount to expand interior to left
     * @param [expandDown] {number} -Optional amount to expand interior downwards
     * @param [isUnClipped] {boolean} -if true, use entire SVG drawing area/paper
	 * @returns {string}
	 */
	getPlotInteriorClipRect( expandRight=0, expandUp=0, expandLeft=0, expandDown=0, isUnClipped=false ) {
        if( isUnClipped ) { // use entire paper
            return '0,0,'+ // use entire paper
                this.paper.width.toFixed(0)+','+
                this.paper.height.toFixed(0);
        } else { // else clip to plot interior bounds, plus expansion
            return (
                (this.bounds.x - expandLeft).toFixed( 0 ) + ',' +
                (this.bounds.y - expandUp).toFixed( 0 ) + ',' +
                (this.bounds.width + expandLeft + expandRight).toFixed( 0 ) + ',' +
                (this.bounds.height + expandUp + expandDown).toFixed( 0 ));
        }
	}

    getProgressIndicatorPoint() {
	    "use strict";
        return ({x: this.bounds.centerX(), y: this.bounds.y + this.bounds.height * 0.33});
    }

	/**
	 * Get information on multiple attributes used by the current plot, if any.
	 * @returns {Dyn.CombinedAttributes|null}
	 */
	getAxisMultiAttributes() {
		"use strict";
		var combinedAttributes = null;

		if( this.plotShaper.numIconsPerCase > 1 ) {
			var plotEl = this.plotShaper.findElement( Dyn.EPlotControl.kControlsShape );
			if( plotEl ) {
				if( plotEl.numAttributesUsedX > 1 ) {
					combinedAttributes = this.xAxis.allAttributes;
				} else if ( plotEl.numAttributesUsedY > 1 ) {
					combinedAttributes = this.yAxis.allAttributes;
				}
			}
		}
		Dyn.assert( combinedAttributes === null || combinedAttributes.countActiveIncludingDualAxis() >= 2 );
		return combinedAttributes;
    }

	/**
	 * Get data on which case color scheme to use for the current plot, used by calcCaseColor().
	 */
	getColorScheme() {
		"use strict";
		var colorScheme = {},
			multiAttrs = this.getAxisMultiAttributes(),
            selectedAttribute = this.dataSet.getSelectedAttribute(),
            colorAttribute = this.rightMargin.getAttribute();
		Dyn.assert( colorAttribute===selectedAttribute, "legend should have been updated by now");
        if( colorAttribute !== selectedAttribute ) {
            this.rightMargin.setSelectedAttributeLegend( selectedAttribute );
            colorAttribute = this.rightMargin.getAttribute(); // right margin hold legend/color attribute
        }

		colorScheme.colorAttribute = this.rightMargin.isColorAxis() ? colorAttribute : null;
		colorScheme.multAttributesRef = multiAttrs;
		colorScheme.useXAxis = multiAttrs && (multiAttrs.id === this.xAxis.isHorizontal);
		colorScheme.isMultiAttribute = ( multiAttrs && ! colorScheme.colorAttribute );
        colorScheme.isNumericInterval = this.plotShaper.hasElement( Dyn.EPlotControl.kControlsNumericIntervals );
        colorScheme.isIncompleteAxis = this.xAxis.isIncompleteAxis() || this.yAxis.isIncompleteAxis() || this.rightMargin.isIncompleteAxis();
        colorScheme.caseFillColor = (this.dataSet.caseSampler.isSampleSelected() ?
                Dyn.plotUtil.kCaseInSampleColor : ( this.dataSet.caseSampler.isDistributionSelected() ?
                Dyn.plotUtil.kCaseInDistributionColor :
                Dyn.plotUtil.kCaseColor ));
        colorScheme.useDeselection = this.dataSet.selectedCases.length > 0;
        colorScheme.backgroundBrightness = Dyn.colorUtil.getColorBrightness( Dyn.plotUtil.normalDropColor );

		return colorScheme;
    }

	/**
	 * Calculate the fill and border colors of the given case icon/bar/pie-slice using the legend attribute's
     * color scheme and case value. If no color attribute, but we have multiple attributes, get a multi-attribute color scheme.
	 * @param casePosition
	 * @param colorScheme
	 * @param isSelected
	 * @returns { fillColor:Dyn.colorUtil.HSB_Color, borderColor:string }
	 */
	calcCaseColor( casePosition, colorScheme, isSelected ) {
		let fillColor, borderColor;

		if( colorScheme.isMultiAttribute ) {
		    // multi-attribute but no legend: color by the attribute that this case is associated with
            let attribIndex = (colorScheme.useXAxis ? casePosition.xAttrIndex : casePosition.yAttrIndex),
                whichAttribute = colorScheme.multAttributesRef.getActiveAttribute( attribIndex );
            Dyn.assert(whichAttribute);
            fillColor = whichAttribute.multiAttrFillColor;
        } else if( colorScheme.isIncompleteAxis ) {
            // incomplete axis colors
            fillColor = new Dyn.colorUtil.SVG_Color( Dyn.plotUtil.kCaseMissingAxisColor );
		} else {
			// legend attribute color or no case color
			Dyn.assert( casePosition.caseIndex >= 0, "invalid case index" );
            fillColor = Dyn.colorUtil.calcCaseColor( casePosition.caseIndex, isSelected, colorScheme );
		}
        if( casePosition.isVisible && casePosition.sectionIndex >= 0 ) {
            // if case color is for bar section or pie slice, also determine text and border colors more carefully
            Dyn.colorUtil.calcColorBrightness( fillColor );
            this.plotShaper.updateSectionColor( fillColor, casePosition.cellIndex, casePosition.sectionIndex );
            borderColor = this.plotShaper.iconStyler.getBorderColor( isSelected, fillColor.colorString, fillColor.b255 );
        } else {
            borderColor = this.plotShaper.iconStyler.getBorderColor( isSelected, fillColor.colorString );
        }
		return { fillColor:fillColor, borderColor:borderColor };
    }

	/**
	 * Draw a marquee selection rectangle at the given coordinates.
	 * @param x1
	 * @param y1
	 * @param width
	 * @param height
	 * @param {Boolean} addToSelection -- if true add to existing selection rather than replacing it
	 */
	marqueeSelect( x1, y1, width, height, addToSelection ) {
		"use strict";

		// start the cache of case bounding boxes, if called for first time since mouse down
		if ( ! this.isInMarqueeSelect() ) {
		    this.startViewMode( {
                mode: "marquee-select",
                bboxes: [],
                batchSize: Math.max( 200, Math.round( this.icons.length / 10 ) ), // tune this amount for responsive update while dragging
                addToSelection: addToSelection
            });
		}

        // show and update the rectangle
        this.marqueeSelectRect.attr( {
            'x': x1, 'y': y1, 'width': width, 'height': height
        } ).show().toFront();

		// start updating cases
		this.marqueeSelectUpdate( false );
    }

	marqueeSelectUpdate( isFinalUpdate ) {
		"use strict";

		//Dyn.time( 'marqueeSelectUpdate: update bboxes', 'Start' );
		Dyn.assert( this.isInMarqueeSelect() );

		var dataSet = this.dataSet,
			casesSelectedData = {
                ids: [],
                object: "plot",
                disableToggle: true, // don't auto-open Case Card
                addToSelection: this.viewModeData.addToSelection,
                isInDrag: true, // request extra feedback from ProgressIndicator, because updates are slow while mouse down
                isMarqueeSelectEnd: Boolean(isFinalUpdate)
			},
			scan = this.viewModeData,
			marqueeBBox,
			elementBBox,
			iconIndex, caseIndex, endIndex, icon;

		// add another batch of case bounding boxes
		//		Note: SLOW, especially in firefox, about 1ms per case, so 5 seconds to scan 5,000 cases.
		//		So we limit our scanning to a smaller number of cases each time.
		//		Testing in Chrome, it seems to cache this and dramatically speed up on second iteration, until icons change again.
		iconIndex = scan.bboxes.length;
		endIndex = isFinalUpdate ? this.icons.length : Math.min( iconIndex + scan.batchSize, this.icons.length );
		for( ; iconIndex<endIndex; ++iconIndex ) {
            icon = this.icons[iconIndex];
            scan.bboxes.push( icon ? icon.getBBox() : null );
		}
		//console.log("marqueeSelectUpdate() "+scan.bboxes.length+" bboxes");
		//Dyn.time( 'marqueeSelectUpdate: update bboxes', 'End' );

		marqueeBBox = this.marqueeSelectRect.getBBox();

		// intersect marquee with elements to get a list of cases inside rectangle
		// 		Note: if we had a routine to intersect the BBox with the pie slice path,
		//		that would be much more accurate. But we don't have one, and marquee
		//		selection is not very useful for pie charts, so we don't worry about it (CDM 2015-06-16)
		//Dyn.time( 'marqueeSelectUpdate:calc list of selected cases', 'Start' );
		endIndex = scan.bboxes.length;
		for( iconIndex=0; iconIndex<endIndex; ++iconIndex) {
			elementBBox = scan.bboxes[iconIndex];
			if ( elementBBox && Raphael.isBBoxIntersect( elementBBox, marqueeBBox ) ) {
				caseIndex = this.icons[iconIndex].data( 'caseIndex' ); // icon index to case index, may be multiple icons per case
				// If the case is filtered or missing, don't add it to selected data
				if( dataSet.isCaseNonMissingNonFiltered( caseIndex )) {
					casesSelectedData.ids.push( caseIndex );
				}
			}
		}
		//Dyn.time( 'marqueeSelectUpdate:calc list of selected cases', 'End' );

		// update the case selection
		//Dyn.time( 'marqueeSelectUpdate:update dataset selection', 'Start' );
		mediator.publish( "records:casesSelected", casesSelectedData );
		//Dyn.time( 'marqueeSelectUpdate:update dataset selection', 'End' );
    }

	marqueeSelectEnd() {
		"use strict";
		Dyn.time( 'marqueeSelectEnd', 'Start' );
		var plotView = this;

		// final update of selection rectangle
		if( this.isInMarqueeSelect() )
			this.marqueeSelectUpdate( true );
		this.viewModeData = null; // clear cache and mark end of select (see isInMarqueeSelect())

		// hide marquee rectangle
		if( this.progressIndicator.isRunning()) {
			// hide later, when progress indicator is done
			this.progressIndicator.queueStopFunction( function(){
				plotView.marqueeSelectRect.hide();
			}, 'marqueeSelectRect.hide()');
		} else {
			// else hide immediately
			this.marqueeSelectRect.hide();
		}
		Dyn.time( 'marqueeSelectEnd', 'End' );
    }

    /**
     * Validate that the plot can enter Draw mode for drawing or placing annotations.
     * @param plotElement
     * @returns {boolean}
     */
	wantDrawToolMode( plotElement ) {
		// only Dyn.AnnotateLayerElement starts the drawing mode, but this is extendable
		return( plotElement.primaryType === Dyn.EPlotControl.kControlsAnnotations );
    }

	drawToolModeAddPoint( x, y, event ) {
		"use strict";
		var drawElement = this.viewModeData && this.viewModeData.plotElement,
			drawOK = this.wantDrawToolMode( drawElement ) && (this.plotShaper.plotElements.indexOf( drawElement ) >= 0);
		Dyn.assert( drawOK );
		if( drawOK ) {
			drawElement.addPoint( this.viewModeData.adornments, x, y, this.xAxis, this.yAxis, event );
		}
    }

    drawToolModePostAddPoint() {
        var drawElement = this.viewModeData && this.viewModeData.plotElement,
            drawOK = this.wantDrawToolMode( drawElement ) && (this.plotShaper.plotElements.indexOf( drawElement ) >= 0);
        Dyn.assert( drawOK );
        if( drawOK ) {
            // trigger deferred call to startViewMode("text-editing") ?
            drawElement.doAfterAddingPoints( this.viewModeData.adornments, this.xAxis, this.yAxis );
        }
    }

    /**
     * Validate that the plot can enter drag-map mode.
     * @param plotElementOrShaper
     * @returns {boolean}
     */
    wantDragMapMode( plotElementOrShaper ) {
        "use strict";
        var isPlotElement = Dyn.PlotElement.prototype.isPrototypeOf(plotElementOrShaper),
            isPlotShaper = Dyn.PlotShaper.prototype.isPrototypeOf(plotElementOrShaper);
       if( isPlotElement ) {
           let mapElement = plotElementOrShaper;
           return( mapElement.primaryType === Dyn.EPlotControl.kControlsMap && mapElement.isInDragMode );     // true if given element is a map
       } else if ( isPlotShaper ) {
           let mapElement = plotElementOrShaper.findElement( Dyn.EPlotControl.kControlsMap );
           return( mapElement && mapElement.isInDragMode );
       }
       return false;
    }

    /**
     * Validate that the plot can enter or remain in "drag-map" mode for repositioning the map plot.
     * @returns {boolean}
     */
    canEnterOrExitDragMapMode() {
        "use strict";
        // returns true only if we have started drag mode via startViewMode(), and the map element is still in the plot.
        var mapElement = this.viewModeData && this.viewModeData.plotElement,
            mapIsInPlot = (this.plotShaper.plotElements.indexOf( mapElement ) >= 0);
        return Boolean( mapElement && mapIsInPlot );
    }

	//endDrawMode() {
	//	"use strict";
	//	this.viewModeData = null; // erase temp data to mark end of drawing mode, see isInFreehandLineMode()
	//	this.drawModeCoverRect.hide(); // hide the cover rectangle that responds to the mouse
	//},

    /**
     * Start text editing mode, so that we can detect clicks on the plot view, to stop editing
     * of elements in our paper (Raphaël.InlineTextEditing).  Unlike clicks on other elements outside
     * this view, we need to manually call the element's blur() function.
     * @param editedElement
     */
    startViewMode( data ) {
	    "use strict";
        // clear any previous modes
        this.setDefaultViewMode( "no-exceptions");

        // now start new mode if any
        switch( data.mode ) {
        case "marquee-select":
            this.viewModeData = {
                mode: data.mode,
                bboxes: data.bboxes,
                batchSize: data.batchSize,
                addToSelection: data.addToSelection
            };
            break;
            case "draw-tool": // rename to "draw-tool" to distinguish from Dyn.preferences.isDrawOnly()?
            Dyn.assert( this.wantDrawToolMode( data.plotElement ));
            this.viewModeData = {
                mode: data.mode,
                plotElement: data.plotElement,
                adornments: this.adornments.getAdornmentsArray( data.plotElement.primaryType )
            };
            this.drawModeCoverRect.show().toFront(); // show the cover rectangle that responds to the mouse
            break;
        case "text-editing":
            this.viewModeData = {  // start text edit mode
                mode: data.mode,
                textElement: data.textElement,
                stopEditing: data.stopEditing
            };
            break;
        case "drag-map":
            this.viewModeData = {  // start drag-map mode (which disables marquee-select)
                mode: data.mode,
                plotElement: data.plotElement
            };
            if( this.canEnterOrExitDragMapMode()) {
                this.viewModeData.plotElement.setDragMapMode( true );
            }
            Dyn.assert( this.canEnterOrExitDragMapMode());
            break;
        default:
            Dyn.assert( false, "unknown view mode "+data.viewModeData );
            // note: default also leaves all special modes cleared
        }
        console.log( "PlotView:startViewMode "+(this.viewModeData ? this.viewModeData.mode : "default")+" START");
    }

    /**
     * Stop the current view mode and revert to the default select mode.
     * @param modeData.mode {string} - only stop if we are in the given mode
     */
    stopViewMode( modeData ) {
	    "use strict";

	    if( this.viewModeData ) {
            var stopCondition = (modeData && modeData.mode),
                isConditionalStop = Boolean( stopCondition ),
                shouldStop = (isConditionalStop ? ( stopCondition === this.viewModeData.mode) : true);

	        if( shouldStop ) {
                console.log( "PlotView:stopViewMode STOP " + this.viewModeData.mode );
                switch( this.viewModeData.mode ) {
                case "marquee-select":
                    this.marqueeSelectEnd();
                    break;
                case "draw-tool":
                    this.drawModeCoverRect.hide(); // hide the cover rectangle that responds to the mouse;
                    break;
                case "text-editing":
                    // if Raphael element is using Raphaël.InlineTextEditing .startEditing()
                    // call element's custom function to end editing
                    Dyn.assert( this.viewModeData.stopEditing );
                    this.viewModeData.stopEditing();
                    break;
                case "drag-map":
                    if( this.canEnterOrExitDragMapMode() ) {
                        this.viewModeData.plotElement.setDragMapMode( false );
                    }
                    break;
                default:
                    Dyn.assert( false, "unknown view mode " + this.viewModeData );
                }
                // erase the temporary mode data and revert to the default view mode.
                this.viewModeData = null;
            } else {
                console.log( "PlotView:stopViewMode IGNORE non-matching condition " + stopCondition );
            }
        }
    }

    /**
     * Clear any special view modes, go back to the default.
     * The default is selection mode for most plots.
     */
    setDefaultViewMode( exceptions ) {
        "use strict";
        var shouldStayInDragMapMode = ( // allow the default view mode for maps to be drag-map
                this.isInDragMapMode() &&
                this.canEnterOrExitDragMapMode() &&
                (exceptions !== "no-exceptions")); // except if caller does not allow it

        if( this.isInMarqueeSelect() || // only expected at mouse up
            this.isInDrawMode() ||      // generally happens if line created but no mouse down yet
            this.isInTextEditMode() ||  // stop editing text of title or annotation
            (this.isInDragMapMode() && ! shouldStayInDragMapMode)    // revert to default select mode
            // NOTE: add other mode checks as needed, here
        ) {
            this.stopViewMode();
        }
        Dyn.assert( (this.viewModeData === null) || shouldStayInDragMapMode );
    }

    /**
     * return true if marqueeSelect() has been called 1+ times,
     * but not followed yet by marqueeSelectEnd().  Should be true during mouse down of marquee select.
     * @returns {boolean}
     */
    isInMarqueeSelect() {
        "use strict";
        return Boolean( this.viewModeData && this.viewModeData.mode==="marquee-select" );
    }

    /**
     * Returns true if an annotation drawing element has been added and we are in annotation drawing mode.
     * @returns {boolean}
     */
    isInDrawMode() {
        "use strict";
        return Boolean( this.viewModeData && this.viewModeData.mode==="draw-tool" ); // see startViewMode();
    }

    isInTextEditMode() {
	    "use strict";
	    return Boolean( this.viewModeData && this.viewModeData.mode==="text-editing" ); // see startViewMode();
    }

    isInDragMapMode() {
        "use strict";
        return Boolean( this.viewModeData && this.viewModeData.mode==="drag-map" ); // see startViewMode();
    }

    /** do we enable hovering/tooltip over cases now?  */
    isDraggingAttribute() {
        return DragDropManager.isInDrag(); // not if dragging attributes
    }

	/**
	 * show or update the color glow over the given plot element
	 * @param iconElement
	 */
	showHoverGlow( iconElement ) {
		"use strict";
		var bbox = iconElement.getBBox(),
			cx = bbox.cx,
			cy = bbox.cy;
		// if no glow, or icon element center position has moved
		if( (! this.caseHoverGlow) ||
			(this.caseHoverGlow.data("cx") !== cx ||
			 this.caseHoverGlow.data("cy") !== cy )) {

			this.hideHoverGlow(); // hide old glow if any

			this.caseHoverGlow = iconElement.glow( {color: Dyn.plotUtil.kCaseHoverBorderColor, width: 10} );
			this.caseHoverGlow.toFront();
			this.caseHoverGlow.data( "cx", cx );
			this.caseHoverGlow.data( "cy", cx );
			iconElement.toFront(); // element in front to avoid glow interfering with drag/hover
            this.plotShaper.layerAdornments(); // adornments in front in case of count sections in pie/bar
			// console.log( "showHoverGlow " +cx+" "+cy );
		}
    }

	/**
	 * hide the glow if visible (undo showHoverGlow())
	 */
	hideHoverGlow() {
		"use strict";
		if( this.caseHoverGlow ) {
			this.caseHoverGlow.remove();
			//console.log("hideHoverGlow");
		}
		this.caseHoverGlow = null;
    }

    /**
     * Return true if the attribute is used or displayed in the plot.
     * @param attributeID {string|Dyn.Attribute.id}
     * @returns {boolean}
     */
    isAttributeInPlot( attributeID ) {
        const selectedAttribute = this.dataSet.getSelectedAttribute(),
              orderByAttribute = this.dataSet.getOrderByAttribute();
	    return Boolean(
	        this.xAxis.isAttributeOnAxis( attributeID ) ||
            this.yAxis.isAttributeOnAxis( attributeID ) ||
            (this.y2Axis && this.y2Axis.isAttributeOnAxis( attributeID )) ||
            this.rightMargin.isAttributeInUse( attributeID ) || // may not be the selected attribute (if updates needed)
            ( selectedAttribute && attributeID === selectedAttribute.id ) || // displayed in legend
            ( orderByAttribute && attributeID === orderByAttribute.id ) // displayed in "ordered by" display (see updateExcludedCasesCount())
        );
    }

    isAnyAttributeInPlot() {
        return Boolean(
            this.dataSet.getSelectedAttribute() ||
            this.dataSet.getOrderByAttribute() ||
            this.xAxis.isNullAxis()===false ||
            this.yAxis.isNullAxis()===false
        );
    }

    /**
     * Get the case data needed to sonify the case value for Audio trace.
     * @param caseIndex
     * @param iconIndex
     * @returns {{value: number}}
     */
    getCaseSonificationData( caseIndex, iconIndex ) {
        let noteData,
            colorAttribute = this.rightMargin.getColorAttribute(),
            plotElement = this.plotShaper.findElement( Dyn.EPlotControl.kControlsShape );

        if( plotElement && plotElement.getSonificationDataForCase ) {
            // let the plot element determine the note to use
            let position = this.casePositions[iconIndex]; // case/icon data set by plotElement.calcPosition()
            noteData = plotElement.getSonificationDataForCase( caseIndex, position.cellIndex, position.sectionIndex );
        }
        if( ! noteData ) {
            if (colorAttribute) {
                // else let the color attribute determine the note to use
                noteData = this.getCaseAttributeSonificationData(colorAttribute, caseIndex);
            } else {
                // else play the missing value sound
                noteData = Dyn.sound.makeNote();
            }
        }
        return noteData;
    }

    /** get the data needed to sonify one case attribute value */
    getCaseAttributeSonificationData( attribute, caseIndex ) {
        let noteData = Dyn.sound.makeNote(), // default to all data undefined to play missing value note
            value = attribute.cases[caseIndex],
            stats = attribute.statsPreFilter;
        if( attribute.hasNoteStringData()) {
            noteData.noteStringToNote( value );
        } else {
            switch (Dyn.dataUtil.getDataType(value)) {
                case 'number': {
                    noteData.numericValueToNote(value, stats.min, stats.max);
                }   break;
                case 'string': {
                    noteData.categoryIndexToNote(
                        stats.categories.indexOf(value),
                        stats.categories.length);
                }   break;
            }
        }
        return noteData;
    }

    getIconAxisSonficationData( iconIndex, axisCode ) {
        let p = this.casePositions[ iconIndex ],
            attribute = (axisCode=='C') ? this.rightMargin.getAttribute() :
                       ((axisCode==='X') ? this.xAxis.getAttribute( p.xAttrIndex ) :
                                        this.yAxis.getAttribute( p.yAttrIndex ) ),
            noteData;
        if( attribute ) { // get the note if possible, otherwise missing
            noteData = this.getCaseAttributeSonificationData( attribute, p.caseIndex );
        } else {
            noteData = Dyn.sound.makeNote();
        }
        return noteData;
    }

    /** get the data needed to sonify one category of an attribute */
    getAttributeCategorySonficationData( attributeID, category, isNumericInterval ) {
        let noteData = Dyn.sound.makeNote(), // default to all data undefined to play missing value note
            attribute = this.dataSet.getAttributeByID( attributeID );
        if( attribute && category ) {
            let stats = attribute.statsPreFilter,
                categories = (isNumericInterval && attribute.hasNumericIntervals())?
                        attribute.numericIntervals.categories  :
                        stats.categories,
                numCategories = categories.length,
                categoryIndex = categories.indexOf( category );
            noteData.categoryIndexToNote( categoryIndex, numCategories );
        }
        return noteData;
    }

    /**
     * Get the data needed to sonify the current plot.
     * @returns {class NoteSequence}
     */
    getPlotSonificationData() {
        // add 0+ sonfication options for the current plot
        let noteSequence = Dyn.sound.makeNoteSequence();
        this.plotShaper.getPlotSonificationData( noteSequence );

        return noteSequence;
    }

    /**
     * Caller has advanced one note in the plot sonification, so here we
     * update the plot view to show the data value being played, e.g.
     * the bar length, the case value, etc.
     * AND we return note data similar to
     * @param soundData -- data from getPlotSonificationData()
     * @returns {{}}
     */
    drawNote( noteSequence, drawIndex ) {
        Dyn.assert( noteSequence.numNotes() >= 0 && drawIndex >= 0 );
        console.log("drawNote", drawIndex );
        switch (noteSequence.data.type) {
            case 'caseValues':
                return this.drawNote_caseValues( noteSequence, drawIndex );
            case 'dataRects':
                return this.drawNote_dataRects( noteSequence, drawIndex );
            case 'categoryValues':
                return this.drawNote_categoryValues( noteSequence, drawIndex );
            default:
                return Dyn.sound.makeNote(); // do nothing; return missing value sound
        }
    }

    /** update plot to visualize playing one note,
     *  when sonifying sorted cases values of an attribute on the Y-axis */
    drawNote_caseValues( noteSequence, drawIndex  ) {
        if( noteSequence.data.iconIndices && drawIndex >= 0 && drawIndex < noteSequence.data.iconIndices.length ) {
            // get note from a sorted list of case indices, and values of the y-axis attribute
            let iconIndex = noteSequence.data.iconIndices[ drawIndex ],
                p = this.casePositions[iconIndex],
                caseIndex = p.caseIndex;
            if( Number.isFinite( caseIndex )) { // select the case if possible, otherwise missing
                mediator.publish( "records:casesSelected", { object:"plot", ids:[ caseIndex ], noAnimation:true });
            }
        } else {
            mediator.publish( "records:casesSelected", { object:"plot", ids:[] }); // deselect cases at end
        }
    }

    /** update plot to visualize playing one note,
     *  when sonifying sorted cases values of an attribute on the Y-axis */
    drawNote_categoryValues( noteSequence, drawIndex  ) {
        if( noteSequence.data.dataValues && drawIndex >= 0 && drawIndex < noteSequence.data.dataValues.length ) {
            // get note from a sorted list of case indices, and values of the y-axis attribute
            let c = noteSequence.data.categories[ drawIndex ],
                v = noteSequence.data.dataValues[ drawIndex ];
            if( noteSequence.data.attributeID ) {
                var attribute = this.dataSet.getAttributeByID( noteSequence.data.attributeID ),
                    isNumericInterval = attribute.hasNumericIntervals(),
                    data = { attributeID:noteSequence.data.attributeID, category:c, isNumericInterval:isNumericInterval };
                // select the cases with the given category for this attribute
                mediator.publish( "records:casesSelectedByCategory", data );
            }
        } else {
            mediator.publish( "records:casesSelected", { object:"plot", ids:[] }); // deselect cases at end
        }
    }

    /** update plot to visualize playing one note,
     *  when sonifying values of bars in a plot. */
    drawNote_dataRects( noteSequence, drawIndex  ) {
        //let noteData = Dyn.sound.makeNote(),
        let isValidRect = false;
        if( noteSequence.data.dataRects && drawIndex >= 0 && drawIndex < noteSequence.data.dataRects.length ) {
            // get note from a sorted list of case indices, and values of the y-axis attribute
            let r = noteSequence.data.dataRects[ drawIndex ];
            isValidRect = true;
            this.sonificationRect.attr({ x:r.x, y:r.y, height:r.height, width:r.width }).show();
        }
        if( !isValidRect ) {
            this.sonificationRect.hide();
        }
    }

    /**
     * Get a reference to the given case icon so we can glow it along with some plot element,
     * e.g. Dyn.PlotElement.addGlow([ caseIcon, showSquareIcon, ...]);
     * Rather than use a slow search for the right icon, here we assume that icons are ordered
     * in our view in the way that Dyn.PlotElementBase.calcPositions() does it.
     */
    getCaseIconForGlow( caseIndex, attrIndex ) {
        let numCasesInDataSet = this.plotShaper.numCasesInDataSet,
            iconIndex = (attrIndex * numCasesInDataSet) + caseIndex;
        if( Dyn.mathUtil.isInIntegerRange( iconIndex, 0, this.icons.length )
            && (this.icons.length === this.casePositions.length )
            && (this.casePositions[iconIndex].caseIndex === caseIndex )) {
            Dyn.assert( (attrIndex === this.casePositions[iconIndex].xAttrIndex )
                ||      (attrIndex === this.casePositions[iconIndex].yAttrIndex ));
            return this.icons[iconIndex];
        }
        return null;
    }

    /**
     * Return boolean value if dual axis is possible with active plot type
     * @param plotelement
     * @returns {boolean}
     */
     isDualAxisAllowed(){
        "use strict";
        let flag = false;
        let that = this;
        $.each( Dyn.plotView.plotShaper.plotElements, function( index, value ) {
            if((that.yAxis.isNumericAxis() && that.yAxis.isNumericAxis()) && [Dyn.EPlotControl.kControlsDotPlot, Dyn.EPlotControl.kControlsLineGraphSingle, Dyn.EPlotControl.kControlsLineAndCategoryGraph ].includes(value.primaryType)){
             flag = true;
            }
        });
        return flag;
    }

    /** is a focusable element within the plot the current keyboard focus (document.activeElement?).
     * Used to help the paste handlers avoid overriding paste for non-plot objects in the document. */
    hasKeyboardFocus( activeElement ) {
        //console.log( ( this.plotObj.has(activeElement).length > 0 ) ? "plot element HAS keyboard focus":"focus is NOT a plot ELement");
        return( this.plotObj.has(activeElement).length > 0 ); // true if activeElement has plot container as an ancestor
    }

    setBackgroundFocus() {
        // #2272 set the plot wrapper as focus so hasKeyboardFocus() returns true,
        // to help the paste handlers avoid overriding paste for non-plot objects in the document.\
        Dyn.plotUtil.setAsKeyboardFocus(this.backgroundRect);
    }

} // class PlotView
Dyn.PlotView = PlotView;

/**
 * Object holding sets of adornments (Rafael elements) for the plot,
 * each associated with a single plot element
 * Example:
 * 	{
 * 		controlsReferenceLine: [ {paper.path}, {paper.path} ],
 * 	    controlsScatterPlot: [ {paper.line} ]
 * 	}
 * Each Dyn.PlotElement should create and remove its own adornments.
 * There is only one PlotAdorments object per PlotView object.
 */

class PlotAdornments {

    constructor( paperRef ) {
        this.paper = paperRef; // reference to Dyn.PlotView.paper
    }

	// get or create the array of adornments for the elementType ("controlsLineGraph", etc.)
	getAdornmentsArray( elementType, createIfMissing = true ) {
		Dyn.assert( elementType instanceof String || /controls.*/.test( elementType ));
		if( createIfMissing ) {
            if( !( elementType in this )) {
                this[elementType] = [];
            }
        }
		return this[elementType];
	}

	// remove the array of adornments for the elementType ("controlsLineGraph", etc.)
	removeAdornmentsArray( elementType ) {
		if( elementType in this ) {
			if( this[elementType].length > 0 ) {
				//console.log( "PlotAdornments.removeAdornmentsArray: type=" + elementType + " length=" + this[elementType].length );
			}
			this[elementType].forEach( value => value.remove()); // remove visual element from plot (Rafael paper)
			delete this[elementType]; // remove array of visual elements from adornments
			// we expect that there are no references to visual elements so they are actually destroyed now.
		}
		Dyn.assert( !( elementType in this ));
	}

    // rename and move an adornments array from one type to another
    renameAdornmentsArray( oldElementType, newElementType ) {
        if( oldElementType in this ) {
            let adornmentsArray = this[oldElementType];
            if( adornmentsArray.length > 0 ) {
                //console.log( "PlotAdornments.moveAdornmentsArray: type=" + oldElementType + " length=" + this[oldElementType].length, newElementType );
            }
            this[newElementType] = adornmentsArray;
            delete this[oldElementType]; // remove array of visual elements from adornments
        }
        Dyn.assert( !(oldElementType in this) );
    }

} // class PlotAdorments
Dyn.PlotAdornments = PlotAdornments;

} // wrapDynPlotView
